import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardSection } from '../hooks/useDashboardSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faBox, faShoppingCart, faDollarSign, faChartLine, faCog,
  faBars, faMoon, faSun, faPlus, faTruck, faChartPie, faMobileAlt,
  faCheckCircle, faNairaSign, faEdit, faEye, faTrash, faFilter,
  faSearch, faUpload, faSave, faUser, faEnvelope, faPhone, faMapMarkerAlt,
  faTimes, faCheck, faClipboardList, faWallet, faBell, faSignOutAlt,
  faUserCircle, faShieldAlt, faQuestionCircle, faHome, faCrown, faRocket, faGift,
  faStar, faGem, faHistory, faList, faThLarge, faSpinner, faMoneyBillWave, faHeadset,
  faUniversity, faHeadphones, faStore, faExchangeAlt, faLock, faPaperPlane,
  faExclamationTriangle, faBan, faTrashAlt, faDownload, faTag, faInbox,
} from '@fortawesome/free-solid-svg-icons';
import { sellerAPI, notificationsAPI, walletAPI, categoriesAPI, badgesAPI, userAPI, requestAPI, paymentAPI, productsAPI, swapAPI } from '../services/api';
import { getStoredDarkMode, setStoredDarkMode } from '../utils/theme';
import { uploadMultipleImages, uploadImage, validateImageFile } from '../services/imageUploadService';
import KYCVerification from './KYCVerification';
import Disputes from './Disputes';
import SwapDeals from './SwapDeals';
import EscrowAnalytics from './EscrowAnalytics';
import EscrowSettingsPanel from './EscrowSettingsPanel';
import { useSwap } from '../context/SwapContext';
import KYCStatusBanner from './KYCStatusBanner';
import BankAccountsManager from './BankAccountsManager';
import AddressManagement from './AddressManagement';
import TwoFactorAuth from './TwoFactorAuth';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler,
} from 'chart.js';
import { format, startOfWeek, endOfWeek, subDays } from 'date-fns';
import Toast from './Toast';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import lightLogo from '../assets/autoboy_logo2.png';
import darkLogo from '../assets/autoboy_logo3.png';
import './SellerDashboard.css';
import '../assets/css/settings-modern.css';
import { TableCard, DataTable, THead, Th, Tr, Td, Badge, TableEmpty, TableLoading, IconBtn, statusTone } from './ui/DataTable';
import OrderIMEIButton from './OrderIMEIButton';
import ColorMultiSelect from './ColorMultiSelect';
import IMEISubmissionModal from './IMEISubmissionModal';
import './WalletEnhanced.css';
import '../assets/css/modern-products.css';
import '../assets/css/wallet-modals.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  Filler
);

// Money coming IN to the seller's wallet. The backend records a completed
// sale's escrow release as type "topup" (handlers/pandascrow.go) while other
// release paths use "credit", and a refund is "refund" — all three are money
// in. The wallet list used to test only `=== 'credit'` for its badge while
// testing all three for the icon and the +/- sign, so a real sale showed a
// green "+₦x" next to a badge reading "Debit". One predicate, used by every
// place that needs to answer this question.
const CREDIT_TRANSACTION_TYPES = ['credit', 'topup', 'refund', 'escrow_release', 'sale'];
const isCreditTransaction = (t) =>
  CREDIT_TRANSACTION_TYPES.includes(String(t?.type || '').toLowerCase()) ||
  String(t?.transaction_type || '').toLowerCase() === 'credit';

// Label for the transaction-type badge. "Credit"/"Debit" is the bookkeeping
// direction; a sale is the common case worth naming explicitly.
const transactionTypeLabel = (t) => {
  const type = String(t?.type || '').toLowerCase();
  if (type === 'topup' || type === 'escrow_release' || type === 'sale') return 'Sale';
  if (type === 'refund') return 'Refund';
  if (type === 'withdrawal') return 'Withdrawal';
  return isCreditTransaction(t) ? 'Credit' : 'Debit';
};

const SellerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    swaps, loading: swapLoading, swapMetadata,
    getReceivedSwaps, respondToOffer, declineOffer, fetchSwaps, calculateSwapFees,
    confirmShipment, confirmDelivery,
  } = useSwap();
  // Section lives in the URL (?section=...) so Back returns to the tab
  // you were on — see useDashboardSection.
  const [activeSection, setActiveSection] = useDashboardSection('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredDarkMode());
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [showEditProductForm, setShowEditProductForm] = useState(false);
  const [showViewProductModal, setShowViewProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false);
  const [toast, setToast] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isLoadingEarnings, setIsLoadingEarnings] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);
  const [productsViewMode, setProductsViewMode] = useState('grid'); // 'grid' or 'list'
  const [userProfile, setUserProfile] = useState({
    name: 'Loading...',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    businessAddress: '',
    businessPhone: '',
    bio: '',
    isVerified: false
  });
  const [settingsForm, setSettingsForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    businessName: '',
    businessAddress: '',
    businessPhone: '',
    bio: ''
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Swap requests panel state
  const [swapRequestsTab, setSwapRequestsTab] = useState('p2p');
  const [swapTopupAmounts, setSwapTopupAmounts] = useState({});
  const [swapActionLoading, setSwapActionLoading] = useState(null);
  // Swap whose seller-side IMEI prompt is currently open. Set right after
  // the seller accepts an offer (and reachable again from the card, in case
  // they dismiss the prompt) — their own device's IMEI is collected once,
  // never shown back, and never visible to the buyer.
  const [swapIMEITarget, setSwapIMEITarget] = useState(null);
  const [swapIMEISubmittedIds, setSwapIMEISubmittedIds] = useState([]);
  const [stats, setStats] = useState({
    activeProducts: 0,
    pendingOrders: 0,
    totalSales: 0,
    totalEarnings: 0
  });
  const [earningsData, setEarningsData] = useState({
    totalRevenue: 0,
    monthlyBreakdown: [],
    profitMargin: 0
  });
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [walletAnalytics, setWalletAnalytics] = useState({
    totalEarned: 0,
    totalWithdrawn: 0,
    totalFees: 0,
    totalOrders: 0,
  });
  const [salesAnalytics, setSalesAnalytics] = useState({
    dailySales: [],
    weeklySales: [],
    growthRate: 0,
    totalOrders: 0,
    monthlyRevenue: 0
  });
  const [productAnalytics, setProductAnalytics] = useState({
    topCategories: [],
    topProducts: []
  });
  const [performanceMetrics, setPerformanceMetrics] = useState({
    growthRate: 0,
    averageOrderValue: 0,
    totalOrders: 0,
    sellerRating: 0,
    totalRatings: 0
  });
  const [newProduct, setNewProduct] = useState({
    title: '',
    category: '',
    brand: '',
    model: '',
    color: '',
    colors: [],
    condition: 'new',
    price: '',           // Regular price (shown to buyers)
    sellerPrice: '',     // Wholesale price for sellers (optional)
    description: '',
    specifications: '',
    quantity: '',
    location: '',
    swapAvailable: false,
    tags: ''
  });

  // Device color options for the currently-entered Model — colors are
  // model-specific (an iPhone 13 Pro's real lineup differs from an iPhone
  // 15's), never a generic list shared across every listing.
  const [availableColors, setAvailableColors] = useState([]);
  const [loadingColors, setLoadingColors] = useState(false);

  useEffect(() => {
    const model = newProduct.model?.trim();
    if (!model) {
      setAvailableColors([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setLoadingColors(true);
      try {
        const res = await productsAPI.getDeviceColors(model);
        const colors = res?.data?.colors || res?.colors || [];
        setAvailableColors(Array.isArray(colors) ? colors : []);
      } catch {
        setAvailableColors([]);
      } finally {
        setLoadingColors(false);
      }
    }, 500); // debounce while the seller is still typing the model
    return () => clearTimeout(timeout);
  }, [newProduct.model]);

  // Avatar/Profile Picture state
  const [avatarUploadMethod, setAvatarUploadMethod] = useState('url');
  const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileInputRef = React.useRef(null);

  // Notifications data - now from API
  const [notifications, setNotifications] = useState([]);

  // Wallet data
  const [walletBalance, setWalletBalance] = useState({
    balance: 0,
    heldBalance: 0,
    currency: 'NGN'
  });
  const [isLoadingWallet, setIsLoadingWallet] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    bankAccount: '',
    bankName: '',
    accountName: '',
    selectedBankId: '' // For verified bank accounts
  });
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
  const [userBankAccounts, setUserBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

  // Settings tab
  const [settingsTab, setSettingsTab] = useState('profile');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    orderUpdates: true,
    priceAlerts: true,
    promotions: false,
    newMessages: true,
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordForDeletion, setPasswordForDeletion] = useState('');
  const [exportingData, setExportingData] = useState(false);

  // Badges
  const [userBadges, setUserBadges] = useState([]);
  const [badgePoints, setBadgePoints] = useState(0);

  // Active sessions
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);

  // Products and Orders data from API
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);

  // Want to Buy (WTB) system state
  const [publicWtbRequests, setPublicWtbRequests] = useState([]);
  const [loadingPublicRequests, setLoadingPublicRequests] = useState(false);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [respondingToRequest, setRespondingToRequest] = useState(null);
  const [responseForm, setResponseForm] = useState({
    selectedProductId: '',
    message: ''
  });
  const [submittingResponse, setSubmittingResponse] = useState(false);

  // Bulk operations state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);

  // Fetch all data from API
  useEffect(() => {
    const fetchAllData = async () => {
      // Check if user has authentication token
      const token = localStorage.getItem('authToken');
      if (!token) {
        setToast({
          message: 'Please log in to access the seller dashboard',
          type: 'error'
        });
        setTimeout(() => {
          window.location.href = '/auth';
        }, 2000);
        return;
      }

      // DEBUG: Decode JWT token to check user_type
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));

          // Check if user is actually a seller
          if (payload.user_type !== 'seller') {
            setToast({
              message: `Access Denied: You are not approved as a seller. Your account type is "${payload.user_type}". Please apply to become a seller first.`,
              type: 'error'
            });
            setTimeout(() => {
              window.location.href = '/become-seller';
            }, 3000);
            return;
          } else {
          }
        }
      } catch (e) {
      }

      // Fetch user profile
      try {
        // Fetch seller profile (business info) and user profile (avatar) in parallel
        const [profileResponse, userProfileRes] = await Promise.all([
          sellerAPI.getProfile(),
          userAPI.getProfile().catch(() => null),
        ]);

        const profilePayload = profileResponse?.data || profileResponse;
        if (profilePayload) {
          const profile = profilePayload.profile || {};
          const businessInfo = profilePayload.business_info || {};

          // Get user data from localStorage as fallback
          const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');

          // Avatar lives in the user profile, not seller profile
          const userProfileData = userProfileRes?.data?.user || userProfileRes?.data || {};
          const resolvedAvatar = userProfileData.avatar || userProfileData.profile?.avatar
            || profile.avatar || profilePayload.avatar
            || cachedUser.avatar || '';

          // For sellers, the business name is stored in first_name field (until backend is updated)
          const displayName = profile.business_name ||
            businessInfo.business_name ||
            profile.first_name ||
            'My Shop';

          const profileData = {
            name: displayName,
            firstName: profile.first_name || '',
            lastName: profile.last_name || '',
            email: profilePayload.email || cachedUser.email || '',
            phone: profilePayload.phone || cachedUser.phone || '',
            businessName: profile.business_name || businessInfo.business_name || profile.first_name || '',
            businessAddress: profile.business_address || businessInfo.business_address || '',
            businessPhone: profile.business_phone || businessInfo.business_phone || profilePayload.phone || '',
            bio: profile.bio || '',
            isVerified: profile.verification_status === 'verified' || false,
            avatar: resolvedAvatar,
          };


          setUserProfile(profileData);

          // Initialize settings form with profile data
          setSettingsForm({
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            phone: profileData.phone,
            businessName: profileData.businessName,
            businessAddress: profileData.businessAddress,
            businessPhone: profileData.businessPhone,
            bio: profileData.bio
          });

          // Set seller rating for performance metrics
          const rating = profileResponse.data.rating || 0;
          const totalRatings = profileResponse.data.total_ratings || 0;
          setPerformanceMetrics(prev => ({
            ...prev,
            sellerRating: rating,
            totalRatings: totalRatings
          }));
        }
      } catch (error) {

        // Check if it's an authentication error
        if (error.isAuthError || error.message.includes('Invalid or expired token')) {
          setToast({
            message: 'Your session has expired. Please log in again.',
            type: 'error'
          });
          setTimeout(() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/auth';
          }, 2000);
          return;
        }

        // Get from localStorage as fallback for other errors
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const profile = user.profile || {};

        const displayName = profile.business_name ||
          profile.first_name ||
          user.email ||
          'My Shop';

        const profileData = {
          name: displayName,
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: user.email || '',
          phone: user.phone || '',
          businessName: profile.business_name || profile.first_name || '',
          businessAddress: profile.business_address || '',
          businessPhone: profile.business_phone || user.phone || '',
          bio: profile.bio || '',
          isVerified: profile.verification_status === 'verified' || false,
          avatar: profile.avatar || user.avatar || '',
        };

        setUserProfile(profileData);

        // Initialize settings form with profile data
        setSettingsForm({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
          businessName: profileData.businessName,
          businessAddress: profileData.businessAddress,
          businessPhone: profileData.businessPhone,
          bio: profileData.bio
        });
      }

      // Fetch all data in parallel for better performance
      try {
        setIsLoadingStats(true);
        setIsLoadingProducts(true);
        setIsLoadingOrders(true);

        const [dashboardResponse, productsResponse, ordersResponse, salesRes, productRes, revenueRes] = await Promise.all([
          sellerAPI.getDashboard().catch(() => ({ data: null })),
          sellerAPI.getProducts({ limit: 10 }).catch(() => ({ data: [] })),
          sellerAPI.getOrders({ limit: 10 }).catch(() => ({ data: [] })),
          sellerAPI.getSalesAnalytics().catch(() => ({ data: null })),
          sellerAPI.getProductAnalytics().catch(() => ({ data: null })),
          sellerAPI.getRevenueAnalytics().catch(() => ({ data: null })),
        ]);

        // Process products
        let mappedProducts = [];
        let totalStock = 0;
        const productsPayload = productsResponse?.data || productsResponse;
        if (productsPayload) {
          const productList = productsPayload.products || (Array.isArray(productsPayload) ? productsPayload : []);
          // Filter out deleted/inactive products
          const activeProducts = productList.filter(p => p.is_active !== false && p.deleted !== true);
          mappedProducts = activeProducts.map(product => ({
            ...product, // Keep all original fields including _id
            id: product._id || product.id,
            name: product.title || product.name,
            price: product.price || 0,
            stock: product.quantity || 0,
            sold: product.sold_count || 0,
            status: product.status || 'active',
            image: product.images && product.images[0] ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0].url) : '/api/placeholder/80/80'
          }));

          // Calculate total stock from all products
          totalStock = mappedProducts.reduce((sum, product) => sum + (product.stock || 0), 0);

          setProducts(mappedProducts);
        }

        // Process orders
        let processedOrders = [];
        let pendingOrdersCount = 0;
        let completedOrdersCount = 0;
        let totalRevenue = 0;

        const ordersPayload = ordersResponse?.data || ordersResponse;
        if (ordersPayload) {
          const orderList = ordersPayload.orders || (Array.isArray(ordersPayload) ? ordersPayload : []);
          processedOrders = orderList.map(order => {
            // Extract product info from items array
            let productDisplay = '';
            let totalQuantity = 0;

            if (order.items && order.items.length > 0) {
              totalQuantity = order.items.reduce((sum, item) => sum + (item.quantity || 0), 0);

              if (order.items.length === 1) {
                // Single product: "2x iPhone 13 Pro"
                const item = order.items[0];
                productDisplay = `${item.quantity}x ${item.product_title || 'Product'}`;
              } else {
                // Multiple products: "3 items"
                productDisplay = `${order.items.length} items (${totalQuantity} total)`;
              }
            } else {
              // Fallback to old fields if items array is missing
              productDisplay = order.product_title || order.product_name || 'Product';
            }

            // Extract customer name — mirrors BuyerDashboard/PremiumBuyerDashboard profile shape
            // (profile.first_name / profile.last_name) with all known field variants
            const buyerProfileName = (
              `${order.buyer?.profile?.first_name || order.buyer?.profile?.firstName || ''} ${order.buyer?.profile?.last_name || order.buyer?.profile?.lastName || ''}`.trim()
            );
            const buyerDirectName = (
              `${order.buyer?.first_name || order.buyer?.firstName || ''} ${order.buyer?.last_name || order.buyer?.lastName || ''}`.trim()
            );
            const shippingName = (
              `${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}`.trim()
            );
            const customerName = order.buyer_name
              || order.buyer?.full_name
              || order.buyer?.name
              || buyerProfileName
              || buyerDirectName
              || order.buyer?.username
              || shippingName
              || order.user?.full_name
              || `${order.user?.first_name || ''} ${order.user?.last_name || ''}`.trim()
              || 'Customer';

            return {
              id: order._id || order.id,
              order_number: order.order_number,
              customer: customerName,
              // Platform privacy rule: the seller sees the buyer's name
              // only — no email, no phone. The backend already stopped
              // sending buyer_email/buyer_phone on this endpoint; this used
              // to still read a handful of other fallback paths for it
              // (shipping_address.email, order.user.email, ...), any one
              // of which would have put it right back on screen.
              product: productDisplay,
              items: order.items || [],
              quantity: totalQuantity,
              amount: order.total_amount || order.amount || 0,
              // What the seller actually receives — Product Price − Platform
              // Fee − VAT, attached server-side by models.AttachSellerPayout.
              // Never the order total: that also carries logistics and the
              // buyer's escrow fee, neither of which is the seller's money.
              sellerPayout: order.seller_payout,
              status: order.status || 'pending',
              payment_status: order.payment_status || 'pending',
              date: order.created_at || new Date(),
              special_packaging: !!order.special_packaging,
            };
          });

          // Calculate order statistics
          pendingOrdersCount = processedOrders.filter(order =>
            order.status === 'pending' || order.status === 'processing'
          ).length;

          completedOrdersCount = processedOrders.filter(order =>
            order.status === 'completed' || order.status === 'delivered'
          ).length;

          // Calculate total revenue from completed orders
          totalRevenue = processedOrders
            .filter(order => order.status === 'completed' || order.status === 'delivered')
            .reduce((sum, order) => sum + (order.amount || 0), 0);

          // Newest first. The backend now sorts too, but this keeps the
          // table correct regardless of what any given source returns.
          processedOrders.sort((a, b) => new Date(b.date) - new Date(a.date));
          setOrders(processedOrders);
        }

        // Update stats with calculated values (priority over backend)
        setStats({
          activeProducts: mappedProducts.length || dashboardResponse?.data?.total_products || 0,
          pendingOrders: pendingOrdersCount || dashboardResponse?.data?.pending_orders || 0,
          totalSales: completedOrdersCount || dashboardResponse?.data?.total_orders || 0,
          totalEarnings: totalRevenue || dashboardResponse?.data?.total_revenue || 0
        });

        // Process sales analytics
        if (salesRes?.data) {
          const s = salesRes.data.data || salesRes.data;
          setSalesAnalytics({
            dailySales: s.daily_sales || s.dailySales || [],
            weeklySales: s.weekly_sales || s.weeklySales || [],
            growthRate: s.growth_rate ?? s.growthRate ?? 0,
            totalOrders: s.total_orders || s.totalOrders || completedOrdersCount,
            monthlyRevenue: s.monthly_revenue || s.monthlyRevenue || totalRevenue,
            revenueTrend: revenueRes?.data?.data?.monthly_breakdown || revenueRes?.data?.monthly_breakdown || [],
          });
        }

        // Process product analytics
        if (productRes?.data) {
          const p = productRes.data.data || productRes.data;
          setProductAnalytics({
            topCategories: p.top_categories || p.topCategories || [],
            topProducts: p.top_products || p.topProducts || [],
          });
        }

        // Process revenue analytics — drives earnings section & performance metrics
        if (revenueRes?.data) {
          const r = revenueRes.data.data || revenueRes.data;
          setEarningsData({
            totalRevenue: r.total_revenue || r.totalRevenue || totalRevenue,
            monthlyBreakdown: r.monthly_breakdown || r.monthlyBreakdown || [],
            profitMargin: r.profit_margin ?? r.profitMargin ?? 0,
          });
          setPerformanceMetrics({
            growthRate: r.growth_rate ?? r.growthRate ?? salesRes?.data?.data?.growth_rate ?? salesRes?.data?.growth_rate ?? 0,
            averageOrderValue: r.average_order_value || r.averageOrderValue || (completedOrdersCount > 0 ? totalRevenue / completedOrdersCount : 0),
            totalOrders: r.total_orders || r.totalOrders || completedOrdersCount,
            sellerRating: r.seller_rating ?? r.sellerRating ?? dashboardResponse?.data?.rating ?? 0,
            totalRatings: r.total_ratings || r.totalRatings || dashboardResponse?.data?.total_ratings || 0,
          });
        } else {
          // Derive performance metrics from order data if revenue endpoint unavailable
          setPerformanceMetrics(prev => ({
            ...prev,
            averageOrderValue: completedOrdersCount > 0 ? totalRevenue / completedOrdersCount : 0,
            totalOrders: completedOrdersCount,
            sellerRating: dashboardResponse?.data?.rating ?? 0,
            totalRatings: dashboardResponse?.data?.total_ratings || 0,
          }));
          setEarningsData(prev => ({
            ...prev,
            totalRevenue: totalRevenue || prev.totalRevenue,
          }));
        }
        setIsLoadingAnalytics(false);


      } catch (error) {
        setProducts([]);
        setOrders([]);
      } finally {
        setIsLoadingStats(false);
        setIsLoadingProducts(false);
        setIsLoadingOrders(false);
      }

      setIsLoadingEarnings(false);

      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 300));

      // Fetch notifications
      try {
        const notificationsResponse = await notificationsAPI.getNotifications();
        const notifPayload = notificationsResponse?.data || notificationsResponse;
        if (notifPayload) {
          const notifList = notifPayload.notifications || (Array.isArray(notifPayload) ? notifPayload : []);
          // Ensure it's an array before slicing
          const notifArray = Array.isArray(notifList) ? notifList : [];
          setNotifications(notifArray.slice(0, 10)); // Show last 10
        }
      } catch (error) {
      }

      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 300));

      // Add delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 300));

      // Fetch categories for product form
      try {
        const categoriesResponse = await categoriesAPI.getAll();
        const catPayload = categoriesResponse?.data || categoriesResponse;
        if (catPayload) {
          const catList = catPayload.categories || (Array.isArray(catPayload) ? catPayload : []);
          setCategories(Array.isArray(catList) ? catList : []);
        }
      } catch (error) {
      }

      // Fetch wallet data
      try {
        const balanceResponse = await walletAPI.getBalance();
        const balPayload = balanceResponse?.data || balanceResponse;
        if (balPayload) {
          setWalletBalance({
            balance: balPayload.balance || 0,
            heldBalance: balPayload.held_balance || 0,
            currency: balPayload.currency || 'NGN'
          });
        }
      } catch (error) {
      }
    };

    fetchAllData();
  }, []);

  // Sync avatar preview with userProfile
  useEffect(() => {
    if (userProfile?.avatar) {
      setAvatarPreview(userProfile.avatar);
    }
  }, [userProfile]);

  // Load notification preferences from API
  useEffect(() => {
    const loadNotifPrefs = async () => {
      try {
        const res = await notificationsAPI.getPreferences();
        const data = res?.data || {};
        setNotificationPrefs({
          emailNotifications: data.email_notifications ?? true,
          pushNotifications: data.push_notifications ?? true,
          smsNotifications: data.sms_notifications ?? false,
          orderUpdates: data.order_updates ?? true,
          priceAlerts: data.price_alerts ?? true,
          promotions: data.promotions ?? false,
          newMessages: data.new_messages ?? true,
        });
      } catch {
        // keep defaults on error
      }
    };
    loadNotifPrefs();
  }, []);

  const handleSaveNotifPrefs = async () => {
    try {
      setSavingNotifPrefs(true);
      await notificationsAPI.updatePreferences({
        email_notifications: notificationPrefs.emailNotifications,
        push_notifications: notificationPrefs.pushNotifications,
        sms_notifications: notificationPrefs.smsNotifications,
        order_updates: notificationPrefs.orderUpdates,
        price_alerts: notificationPrefs.priceAlerts,
        promotions: notificationPrefs.promotions,
        new_messages: notificationPrefs.newMessages,
      });
      setToast({ message: 'Notification preferences saved.', type: 'success' });
    } catch {
      setToast({ message: 'Failed to save notification preferences.', type: 'error' });
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({ message: 'New passwords do not match.', type: 'error' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setToast({ message: 'Password must be at least 6 characters.', type: 'error' });
      return;
    }
    try {
      setSavingPassword(true);
      await userAPI.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setToast({ message: 'Password updated successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err?.response?.data?.message || 'Failed to update password.', type: 'error' });
    } finally {
      setSavingPassword(false);
    }
  };

  // Load badges on mount
  useEffect(() => {
    Promise.all([
      badgesAPI.getUserBadges().catch(() => ({ data: null })),
      badgesAPI.getUserRewards().catch(() => ({ data: null })),
    ]).then(([badgesRes, rewardsRes]) => {
      setUserBadges(badgesRes?.data?.badges || []);
      setBadgePoints(rewardsRes?.data?.total_points || rewardsRes?.data?.points || 0);
    });
  }, []);

  // Load sessions when settings section is active
  useEffect(() => {
    if (activeSection === 'settings') {
      setSessionsLoading(true);
      userAPI.getActiveSessions()
        .then(res => setActiveSessions(Array.isArray(res?.data?.sessions || res?.data) ? (res?.data?.sessions || res?.data) : []))
        .catch(() => setActiveSessions([]))
        .finally(() => setSessionsLoading(false));
    }
  }, [activeSection]);

  const handleTerminateSession = async (sessionId) => {
    try {
      setTerminatingSession(sessionId);
      await userAPI.terminateSession(sessionId);
      setActiveSessions(prev => prev.filter(s => (s.id || s._id) !== sessionId));
      setToast({ message: 'Session terminated.', type: 'success' });
    } catch { setToast({ message: 'Failed to terminate session.', type: 'error' }); }
    finally { setTerminatingSession(null); }
  };

  const handleTerminateAllSessions = async () => {
    if (!window.confirm('Sign out of all other devices?')) return;
    try {
      await userAPI.terminateAllOtherSessions();
      const res = await userAPI.getActiveSessions();
      setActiveSessions(Array.isArray(res?.data?.sessions || res?.data) ? (res?.data?.sessions || res?.data) : []);
      setToast({ message: 'All other sessions terminated.', type: 'success' });
    } catch { setToast({ message: 'Failed to terminate sessions.', type: 'error' }); }
  };

  const handleExportData = async () => {
    try {
      setExportingData(true);
      const response = await userAPI.exportUserData();
      if (response && response.success) {
        setToast({ message: 'Data export initialized. Check your email.', type: 'success' });
      } else {
        setToast({ message: 'Failed to initialize data export.', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Error exporting data.', type: 'error' });
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!passwordForDeletion) {
      setToast({ message: 'Enter password to confirm.', type: 'error' });
      return;
    }

    if (!window.confirm('Irreversible action. Proceed with account deletion?')) return;

    try {
      setDeletingAccount(true);
      const response = await userAPI.deleteAccount(passwordForDeletion);
      if (response && response.success) {
        setToast({ message: 'Account deleted.', type: 'success' });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/';
        }, 3000);
      } else {
        setToast({ message: response?.message || 'Failed to delete account.', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Error deleting account.', type: 'error' });
    } finally {
      setDeletingAccount(false);
    }
  };

  const fetchPublicRequests = async () => {
    try {
      setLoadingPublicRequests(true);
      const res = await requestAPI.getRequests();
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : raw?.requests || raw?.data || [];
      setPublicWtbRequests(list);
    } catch {
      setToast({ message: 'Failed to fetch public buyer requests.', type: 'error' });
    } finally {
      setLoadingPublicRequests(false);
    }
  };

  const handleRespondToRequest = async (e) => {
    e.preventDefault();
    if (!responseForm.selectedProductId) {
      setToast({ message: 'Please select a product to link.', type: 'error' });
      return;
    }

    try {
      setSubmittingResponse(true);

      // Get the product URL from the selected product ID
      const product = products.find(p => (p.id || p._id) === responseForm.selectedProductId);
      if (!product) throw new Error('Product not found');

      const productUrl = `${window.location.origin}/product/${product.id || product._id}`;

      const response = await requestAPI.respondToRequest(respondingToRequest.id || respondingToRequest._id, {
        product_url: productUrl,
        message: responseForm.message
      });

      if (response && response.success) {
        setToast({ message: 'Response sent to buyer!', type: 'success' });
        setShowRespondModal(false);
        setResponseForm({ selectedProductId: '', message: '' });
      }
    } catch (err) {
      setToast({ message: err?.response?.data?.error || 'Failed to send response.', type: 'error' });
    } finally {
      setSubmittingResponse(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedProducts.length === 0) return;

    try {
      setIsProcessingBulkAction(true);
      const promises = selectedProducts.map(id => sellerAPI.updateProduct(id, { status }));
      await Promise.all(promises);

      setToast({ message: `Successfully updated ${selectedProducts.length} products to ${status}.`, type: 'success' });
      setSelectedProducts([]);
      fetchSellerProducts(); // Refresh list
    } catch {
      setToast({ message: 'Failed to update some products.', type: 'error' });
    } finally {
      setIsProcessingBulkAction(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) return;

    try {
      setIsProcessingBulkAction(true);
      const promises = selectedProducts.map(id => sellerAPI.deleteProduct(id));
      await Promise.all(promises);

      setToast({ message: `Successfully deleted ${selectedProducts.length} products.`, type: 'success' });
      setSelectedProducts([]);
      fetchSellerProducts(); // Refresh list
    } catch {
      setToast({ message: 'Failed to delete some products.', type: 'error' });
    } finally {
      setIsProcessingBulkAction(false);
    }
  };

  // Fetch wallet balance, transactions, and earnings data when section is active
  useEffect(() => {
    if (activeSection === 'wallet' || activeSection === 'earnings') {
      fetchWalletBalance();
      fetchWalletTransactions();
      if (activeSection === 'earnings') {
        setIsLoadingEarnings(true);
        Promise.all([
          walletAPI.getAnalytics().catch(() => ({ data: null })),
          sellerAPI.getRevenueAnalytics().catch(() => ({ data: null })),
        ]).then(([analyticsRes, revenueRes]) => {
          if (analyticsRes?.data) {
            const a = analyticsRes.data.data || analyticsRes.data;
            setWalletAnalytics({
              totalEarned: a.total_earned || a.totalEarned || a.total_credited || 0,
              totalWithdrawn: a.total_withdrawn || a.totalWithdrawn || a.total_debited || 0,
              totalFees: a.total_fees || a.totalFees || a.platform_fees || 0,
              totalOrders: a.total_orders || a.totalOrders || 0,
            });
          }
          if (revenueRes?.data) {
            const r = revenueRes.data.data || revenueRes.data;
            setEarningsData({
              totalRevenue: r.total_revenue || r.totalRevenue || 0,
              monthlyBreakdown: r.monthly_breakdown || r.monthlyBreakdown || [],
              profitMargin: r.profit_margin ?? r.profitMargin ?? 0,
            });
          }
        }).finally(() => setIsLoadingEarnings(false));
      }
    }
  }, [activeSection]);

  // Refresh swap data when swap-deals section becomes active
  useEffect(() => {
    if (activeSection === 'swap-deals') {
      fetchSwaps();
    }
  }, [activeSection]);

  // Fetch buyer requests when buyer-requests section becomes active
  useEffect(() => {
    if (activeSection === 'buyer-requests') {
      fetchPublicRequests();
    }
  }, [activeSection]);

  // Refresh analytics when analytics section becomes active
  useEffect(() => {
    if (activeSection === 'analytics') {
      setIsLoadingAnalytics(true);
      Promise.all([
        sellerAPI.getSalesAnalytics().catch(() => ({ data: null })),
        sellerAPI.getProductAnalytics().catch(() => ({ data: null })),
        sellerAPI.getRevenueAnalytics().catch(() => ({ data: null })),
      ]).then(([salesRes, productRes, revenueRes]) => {
        if (salesRes?.data) {
          const s = salesRes.data.data || salesRes.data;
          setSalesAnalytics({
            dailySales: s.daily_sales || s.dailySales || [],
            weeklySales: s.weekly_sales || s.weeklySales || [],
            growthRate: s.growth_rate ?? s.growthRate ?? 0,
            totalOrders: s.total_orders || s.totalOrders || 0,
            monthlyRevenue: s.monthly_revenue || s.monthlyRevenue || 0,
          });
        }
        if (productRes?.data) {
          const p = productRes.data.data || productRes.data;
          setProductAnalytics({
            topCategories: p.top_categories || p.topCategories || [],
            topProducts: p.top_products || p.topProducts || [],
          });
        }
        if (revenueRes?.data) {
          const r = revenueRes.data.data || revenueRes.data;
          setEarningsData(prev => ({
            totalRevenue: r.total_revenue || r.totalRevenue || prev.totalRevenue,
            monthlyBreakdown: r.monthly_breakdown || r.monthlyBreakdown || prev.monthlyBreakdown,
            profitMargin: r.profit_margin ?? r.profitMargin ?? prev.profitMargin,
          }));
          setPerformanceMetrics(prev => ({
            growthRate: r.growth_rate ?? r.growthRate ?? prev.growthRate,
            averageOrderValue: r.average_order_value || r.averageOrderValue || prev.averageOrderValue,
            totalOrders: r.total_orders || r.totalOrders || prev.totalOrders,
            sellerRating: r.seller_rating ?? r.sellerRating ?? prev.sellerRating,
            totalRatings: r.total_ratings || r.totalRatings || prev.totalRatings,
          }));
        }
      }).finally(() => setIsLoadingAnalytics(false));
    }
  }, [activeSection]);

  // Fetch bank accounts when withdrawal modal opens
  useEffect(() => {
    if (showWithdrawModal) {
      fetchUserBankAccounts();
    }
  }, [showWithdrawModal]);

  // Handle navigation from external links (e.g., location state routing)
  useEffect(() => {
    // useDashboardSection already seeds from location.state on mount; the
    // replaceState({}) that used to live here wiped the section out of the
    // history entry, so a later Back could never restore it.
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);
    }
  }, [location]);

  // Check for payment success on page load - Verify payment then update wallet
  useEffect(() => {
    const verifyAndUpdateWallet = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const reference = urlParams.get('reference') ||
        urlParams.get('trxref') ||
        localStorage.getItem('pending_payment_reference');

      if (paymentStatus === 'success' || reference) {
        try {
          // Show verifying message
          setToast({ message: '🔄 Verifying payment...', type: 'info' });

          // Switch to wallet section
          setActiveSection('wallet');

          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          // Verify payment with backend
          const { paymentAPI } = await import('../services/api');

          const verificationResponse = await paymentAPI.verifyPayment(reference);

          if (verificationResponse && verificationResponse.success) {
            const paymentData = verificationResponse.data;

            // Check if payment is for wallet top-up
            if (paymentData.payment_type === 'wallet_topup') {
              // Fetch updated wallet balance
              const walletResponse = await walletAPI.getBalance();

              const walletPayload = walletResponse?.data || walletResponse;
              if (walletPayload) {
                const newBalance = walletPayload.balance || 0;
                setWalletBalance({
                  balance: newBalance,
                  heldBalance: walletPayload.held_balance || 0,
                  currency: walletPayload.currency || 'NGN'
                });

                // Show credit notification with amount
                const topupAmount = parseFloat(paymentData.amount) || 0;
                setToast({
                  message: `💰 Wallet Credited: +₦${topupAmount.toLocaleString()} | New Balance: ₦${newBalance.toLocaleString()}`,
                  type: 'success'
                });
              }

              // Clear pending payment data
              localStorage.removeItem('pending_payment_reference');
              localStorage.removeItem('pending_payment_id');

              // Refresh transactions
              fetchWalletTransactions();
            } else {
              setToast({ message: '✓ Payment verified successfully!', type: 'success' });
            }
          } else {
            setToast({ message: verificationResponse?.message || 'Payment verification failed. Please contact support.', type: 'error' });
          }
        } catch (error) {

          // Clear failed payment references
          localStorage.removeItem('pending_payment_reference');
          localStorage.removeItem('pending_payment_id');

          // Only show error toast if it's not a "failed" status payment
          if (!error.message?.includes('Payment status: failed')) {
            setToast({
              message: error.message || 'Error verifying payment. Please contact support.',
              type: 'error'
            });
          }
        }
      }
    };

    verifyAndUpdateWallet();
  }, []);

  // Chart data - Dynamic from API
  const salesChartData = {
    labels: salesAnalytics.weeklySales.length > 0
      ? salesAnalytics.weeklySales.map(item => item.day || item.label)
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Sales (₦)',
        data: salesAnalytics.weeklySales.length > 0
          ? salesAnalytics.weeklySales.map(item => item.amount || item.sales || 0)
          : [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#22C55E',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        tension: 0.4,
        fill: true,
      }
    ]
  };

  const categoryChartData = {
    labels: productAnalytics.topCategories.length > 0
      ? productAnalytics.topCategories.map(item => item.category || item.name)
      : ['No Data'],
    datasets: [
      {
        data: productAnalytics.topCategories.length > 0
          ? productAnalytics.topCategories.map(item => item.count || item.sales || 0)
          : [0],
        backgroundColor: productAnalytics.topCategories.length > 0
          ? ['#22C55E', '#16A34A', '#4ADE80', '#10B981', '#6B7280']
          : ['#6B7280'],
        borderWidth: 0,
      }
    ]
  };

  const monthlyEarningsData = {
    labels: earningsData.monthlyBreakdown.length > 0
      ? earningsData.monthlyBreakdown.map(item => item.month)
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Earnings (₦)',
        data: earningsData.monthlyBreakdown.length > 0
          ? earningsData.monthlyBreakdown.map(item => item.revenue)
          : [0, 0, 0, 0, 0, 0],
        backgroundColor: '#22C55E',
        borderRadius: 8,
      }
    ]
  };

  useEffect(() => {
    document.body.classList.toggle('AutoboyExpress-dash-dark-mode', isDarkMode);
    // Tailwind's compiled `:where(...)` selector for our custom darkMode config
    // only expands `.tw-dark-mode` to also match descendants (`.tw-dark-mode *`);
    // `.tw-AutoboyExpress-dash-dark-mode` compiles as a self-match-only term, so
    // toggling it on <body> never reaches nested `dark:` utilities (see ui/DataTable.jsx).
    // `tw-dark-mode` is the class that actually cascades — ThemeSync strips it on
    // dashboard routes to avoid storefront bleed, so we re-assert it here.
    document.body.classList.toggle('tw-dark-mode', isDarkMode);
    setStoredDarkMode(isDarkMode);
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const handleSettingsSave = async (e) => {
    e.preventDefault();
    setIsSavingSettings(true);

    try {
      const profileUpdateData = {
        phone: settingsForm.phone,
        profile: {
          first_name: settingsForm.firstName,
          last_name: settingsForm.lastName,
          business_name: settingsForm.businessName,
          business_address: settingsForm.businessAddress,
          business_phone: settingsForm.businessPhone,
          bio: settingsForm.bio
        }
      };

      await sellerAPI.updateProfile(profileUpdateData);

      // Update local userProfile state
      setUserProfile(prev => ({
        ...prev,
        name: settingsForm.businessName,
        firstName: settingsForm.firstName,
        lastName: settingsForm.lastName,
        phone: settingsForm.phone,
        businessName: settingsForm.businessName,
        businessAddress: settingsForm.businessAddress,
        businessPhone: settingsForm.businessPhone,
        bio: settingsForm.bio
      }));

      setToast({ message: '✓ Profile updated successfully!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update profile. Please try again.', type: 'error' });
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      // Clear auth data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');

      // Redirect to auth page
      navigate('/auth');
    }
  };

  const formatCurrency = (amount) => {
    return `₦${amount.toLocaleString()}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      shipped: '#3b82f6',
      delivered: '#22c55e',
      cancelled: '#ef4444',
      active: '#22c55e',
      out_of_stock: '#ef4444',
      draft: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      active: 'Active',
      out_of_stock: 'Out of Stock',
      draft: 'Pending Approval',
      rejected: 'Rejected',
      suspended: 'Suspended',
      inactive: 'Inactive'
    };
    return texts[status] || status;
  };

  const getPaymentStatusColor = (paymentStatus) => {
    const colors = {
      pending: '#f59e0b',
      paid: '#22c55e',
      completed: '#22c55e',
      escrow: '#3b82f6',
      failed: '#ef4444',
      refunded: '#6b7280',
      cancelled: '#9ca3af'
    };
    return colors[paymentStatus] || '#6b7280';
  };

  // `completed` covers two very different situations: a plain card/transfer
  // order that simply finished, and an escrow order whose funds the buyer
  // has now released to the seller. Sellers were told the second case still
  // read "In Escrow" (the release path never updated payment_status — fixed
  // backend-side), and once it does update, "Completed" alone doesn't say
  // the money actually moved. Pass the order and escrow-released orders read
  // "Released".
  const getPaymentStatusText = (paymentStatus, order) => {
    const texts = {
      pending: 'Pending',
      paid: 'Paid',
      completed: 'Completed',
      escrow: 'In Escrow',
      failed: 'Failed',
      refunded: 'Refunded',
      cancelled: 'Cancelled'
    };
    const key = String(paymentStatus || '').toLowerCase();
    if (key === 'completed' && (order?.use_escrow || order?.useEscrow)) return 'Released';
    return texts[key] || paymentStatus;
  };

  const handleProductInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    // Validate number of images
    if (productImages.length + files.length > 10) {
      setToast({
        message: `You can only upload up to 10 images. Currently: ${productImages.length}`,
        type: 'warning'
      });
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of files) {
      const validation = validateImageFile(file, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      });

      if (!validation.valid) {
        setToast({
          message: `${file.name}: ${validation.error}`,
          type: 'error'
        });
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    // Upload images
    setUploadingImages(true);
    setUploadProgress({ current: 0, total: validFiles.length, fileName: '' });

    try {
      const uploadedUrls = await uploadMultipleImages(validFiles, (progress) => {
        setUploadProgress({
          current: progress.index + 1,
          total: progress.total,
          fileName: progress.fileName,
          status: progress.status
        });

        if (progress.status === 'error') {
          setToast({
            message: `Failed to upload ${progress.fileName}: ${progress.error}`,
            type: 'error'
          });
        }
      });

      // Filter out any undefined/null URLs (failed uploads)
      const successfulUrls = uploadedUrls.filter(url => url);

      if (successfulUrls.length > 0) {
        setProductImages(prev => [...prev, ...successfulUrls].slice(0, 10));
        setToast({
          message: `Successfully uploaded ${successfulUrls.length} image(s)`,
          type: 'success'
        });
      }

      if (successfulUrls.length < validFiles.length) {
        setToast({
          message: `${validFiles.length - successfulUrls.length} image(s) failed to upload`,
          type: 'warning'
        });
      }
    } catch (error) {
      setToast({
        message: error.message || 'Failed to upload images. Please try again.',
        type: 'error'
      });
    } finally {
      setUploadingImages(false);
      setUploadProgress({ current: 0, total: 0, fileName: '' });
      // Reset file input
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddImageUrl = () => {
    if (imageUrlInput.trim() && productImages.length < 10) {
      // Validate URL format
      try {
        new URL(imageUrlInput);
        setProductImages(prev => [...prev, imageUrlInput.trim()]);
        setImageUrlInput('');
      } catch (error) {
        setToast({ message: 'Please enter a valid image URL', type: 'error' });
      }
    }
  };

  // Fetch products function
  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const productsResponse = await sellerAPI.getProducts({ limit: 10 });

      const fetchProdPayload = productsResponse?.data || productsResponse;
      if (fetchProdPayload) {
        const productList = fetchProdPayload.products || (Array.isArray(fetchProdPayload) ? fetchProdPayload : []);
        // Filter out deleted/inactive products
        const activeProducts = productList.filter(p => p.is_active !== false && p.deleted !== true);
        const mappedProducts = activeProducts.map(product => ({
          ...product, // Keep all original fields including _id
          id: product._id || product.id,
          name: product.title || product.name,
          price: product.price || 0,
          stock: product.quantity || 0,
          sold: product.sold_count || 0,
          status: product.status || 'active',
          image: product.images && product.images[0] ? (typeof product.images[0] === 'string' ? product.images[0] : product.images[0].url) : '/api/placeholder/80/80'
        }));

        setProducts(mappedProducts);

        // Update active products count in stats
        setStats(prev => ({
          ...prev,
          activeProducts: mappedProducts.length
        }));
      }
    } catch (error) {
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();

    try {
      // Block non-admin users from posting in Accessories category
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (newProduct.category === 'accessories' && currentUser.user_type !== 'admin') {
        setToast({ message: 'Only admins can post in the Accessories category', type: 'error' });
        return;
      }

      // Validate required fields
      if (!newProduct.title || newProduct.title.length < 5) {
        setToast({ message: 'Title must be at least 5 characters', type: 'error' });
        return;
      }
      if (!newProduct.description || newProduct.description.length < 20) {
        setToast({ message: 'Description must be at least 20 characters', type: 'error' });
        return;
      }
      if (!newProduct.price || parseFloat(newProduct.price) <= 0) {
        setToast({ message: 'Price must be greater than 0', type: 'error' });
        return;
      }
      // Validate seller price if provided
      if (newProduct.sellerPrice && parseFloat(newProduct.sellerPrice) <= 0) {
        setToast({ message: 'Wholesale price must be greater than 0', type: 'error' });
        return;
      }
      if (newProduct.sellerPrice && parseFloat(newProduct.sellerPrice) >= parseFloat(newProduct.price)) {
        setToast({ message: 'Wholesale price must be less than regular price', type: 'error' });
        return;
      }
      if (!newProduct.category) {
        setToast({ message: 'Please select a category', type: 'error' });
        return;
      }
      if (!newProduct.location) {
        setToast({ message: 'Please enter location', type: 'error' });
        return;
      }

      // Prepare product data for backend (matching CreateProductRequest)
      const productData = {
        title: newProduct.title,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        currency: 'NGN', // Nigerian Naira
        condition: newProduct.condition,
        brand: newProduct.brand || '',
        model: newProduct.model || '',
        color: newProduct.colors?.[0] || newProduct.color || '',
        colors: newProduct.colors || [],
        category_id: newProduct.category, // This should be a valid category ObjectID
        specifications: newProduct.specifications ?
          { details: newProduct.specifications } : {},
        quantity: parseInt(newProduct.quantity) || 1,
        status: 'active', // Set status to active instead of draft
        location: {
          city: newProduct.location.split(',')[0]?.trim() || newProduct.location,
          state: newProduct.location.split(',')[1]?.trim() || 'Lagos',
          country: 'Nigeria'
        },
        swap_available: newProduct.swapAvailable || false,
        tags: newProduct.tags ? newProduct.tags.split(',').map(t => t.trim()) : [],
        // Only send actual URLs, filter out blob URLs (local previews)
        images: productImages.length > 0
          ? productImages
            .filter(img => {
              const imgStr = typeof img === 'string' ? img : img?.url || '';
              return !imgStr.startsWith('blob:');
            })
            .map(img => typeof img === 'string' ? img : img?.url || img)
          : []
      };

      // Only add seller_price if it has a value
      if (newProduct.sellerPrice && newProduct.sellerPrice.trim() !== '') {
        productData.seller_price = parseFloat(newProduct.sellerPrice);
      }


      // Submit to backend
      const response = await sellerAPI.addProduct(productData);

      if (response) {
        setToast({ message: '✓ Product added successfully!', type: 'success' });
        setShowAddProductForm(false);

        // Reset form
        setNewProduct({
          title: '',
          category: '',
          brand: '',
          model: '',
          color: '',
          colors: [],
          condition: 'new',
          price: '',
          sellerPrice: '',
          description: '',
          specifications: '',
          quantity: '',
          location: '',
          swapAvailable: false,
          tags: ''
        });
        setProductImages([]);

        // Refresh products list
        fetchProducts();
      }
    } catch (error) {
      setToast({
        message: error.message || 'Failed to add product. Please try again.',
        type: 'error'
      });
    }
  };

  // View Product Handler
  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setShowViewProductModal(true);
  };

  // Edit Product Handler
  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setNewProduct({
      title: product.name || product.title || '',
      category: product.category_id || product.category || '',
      brand: product.brand || '',
      model: product.model || '',
      color: product.color || '',
      colors: product.colors?.length ? product.colors : (product.color ? [product.color] : []),
      condition: product.condition || 'new',
      price: product.price || '',
      sellerPrice: product.seller_price || product.sellerPrice || '',
      description: product.description || '',
      specifications: product.specifications?.details || product.specifications || '',
      quantity: product.stock || product.quantity || '',
      location: product.location ? `${product.location.city}, ${product.location.state}` : '',
      swapAvailable: product.swap_available || false,
      tags: product.tags ? product.tags.join(', ') : ''
    });

    // Load existing product images
    if (product.images && Array.isArray(product.images)) {
      const imageUrls = product.images.map(img =>
        typeof img === 'string' ? img : img.url
      ).filter(url => url && url.trim() !== '');
      setProductImages(imageUrls);
    } else {
      setProductImages([]);
    }

    setShowEditProductForm(true);
  };

  // Update Product Handler
  const handleUpdateProduct = async (e) => {
    e.preventDefault();

    if (!selectedProduct) return;

    try {
      // Validate required fields
      if (!newProduct.title || newProduct.title.length < 5) {
        setToast({ message: 'Title must be at least 5 characters', type: 'error' });
        return;
      }
      if (!newProduct.description || newProduct.description.length < 20) {
        setToast({ message: 'Description must be at least 20 characters', type: 'error' });
        return;
      }

      // Validate condition field
      const validConditions = ['new', 'uk_used', 'nigeria_used', 'swap_deal', 'for_sale'];
      if (!validConditions.includes(newProduct.condition)) {
        setToast({
          message: `Invalid condition: ${newProduct.condition}. Must be one of: ${validConditions.join(', ')}`,
          type: 'error'
        });
        return;
      }

      const productData = {
        title: newProduct.title,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        currency: 'NGN',
        condition: newProduct.condition,
        brand: newProduct.brand || '',
        model: newProduct.model || '',
        color: newProduct.colors?.[0] || newProduct.color || '',
        colors: newProduct.colors || [],
        category_id: newProduct.category,
        specifications: newProduct.specifications ? { details: newProduct.specifications } : {},
        quantity: parseInt(newProduct.quantity) || 1,
        status: 'active', // Keep status active
        location: {
          city: newProduct.location.split(',')[0]?.trim() || newProduct.location,
          state: newProduct.location.split(',')[1]?.trim() || 'Lagos',
          country: 'Nigeria'
        },
        swap_available: newProduct.swapAvailable || false,
        tags: newProduct.tags ? newProduct.tags.split(',').map(t => t.trim()) : [],
        // Always send images array (filter out blob URLs)
        images: productImages
          .filter(img => {
            const imgStr = typeof img === 'string' ? img : img?.url || '';
            return !imgStr.startsWith('blob:');
          })
          .map(img => typeof img === 'string' ? img : img?.url || img)
      };

      // Only add seller_price if it has a value
      if (newProduct.sellerPrice && newProduct.sellerPrice.trim() !== '') {
        productData.seller_price = parseFloat(newProduct.sellerPrice);
      }

      // Use the MongoDB _id field
      const productId = selectedProduct._id || selectedProduct.id;


      const response = await sellerAPI.updateProduct(productId, productData);

      if (response) {
        // Close the edit form first
        setShowEditProductForm(false);
        setSelectedProduct(null);

        // Clear form data
        setNewProduct({
          title: '',
          category: '',
          brand: '',
          model: '',
          color: '',
          colors: [],
          condition: 'new',
          price: '',
          sellerPrice: '',
          description: '',
          specifications: '',
          quantity: '',
          location: '',
          swapAvailable: false,
          tags: ''
        });
        setProductImages([]);

        // Show success toast
        setToast({ message: '✓ Product updated successfully! Refreshing...', type: 'success' });

        // Refresh products list after a short delay to show the toast
        setTimeout(async () => {
          await fetchProducts();
          setToast({ message: '✓ Product list refreshed!', type: 'success' });
        }, 1000);
      }
    } catch (error) {
      setToast({
        message: error.message || 'Failed to update product. Please try again.',
        type: 'error'
      });
    }
  };

  // Delete Product Handler
  const handleDeleteProduct = async (product) => {
    if (!window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      return;
    }

    try {
      // Use MongoDB _id field for deletion
      const productId = product._id || product.id;

      // Optimistically remove from local state for instant feedback
      setProducts(prevProducts => prevProducts.filter(p => (p._id || p.id) !== productId));

      const response = await sellerAPI.deleteProduct(productId);

      setToast({ message: '✓ Product deleted successfully!', type: 'success' });

      // Refresh products list to ensure sync with backend
      await fetchProducts();
    } catch (error) {
      setToast({
        message: error.response?.data?.message || error.message || 'Failed to delete product. Please try again.',
        type: 'error'
      });

      // Revert optimistic update by refreshing from backend
      await fetchProducts();
    }
  };

  // Avatar Upload Handlers
  const handleAvatarFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setToast({ message: 'Please select an image file', type: 'error' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setToast({ message: 'Image size should be less than 5MB', type: 'error' });
        return;
      }
      setSelectedAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarUpload = async () => {
    try {
      setUploadingAvatar(true);
      let avatarUrl = '';

      if (avatarUploadMethod === 'url') {
        avatarUrl = userProfile?.avatar || '';
        if (!avatarUrl || !avatarUrl.startsWith('http')) {
          setToast({ message: 'Please enter a valid image URL', type: 'error' });
          setUploadingAvatar(false);
          return;
        }
      } else {
        if (!selectedAvatarFile) {
          setToast({ message: 'Please select an image file', type: 'error' });
          setUploadingAvatar(false);
          return;
        }
        const { url } = await uploadImage(selectedAvatarFile);
        avatarUrl = url;
      }

      // Use userAPI so the avatar is saved to /user/profile — this is what
      // AuthContext reads back on every page load via userAPI.getProfile()
      await userAPI.updateProfile({
        first_name: userProfile?.firstName || '',
        last_name: userProfile?.lastName || '',
        avatar: avatarUrl,
      });
      const updatedProfile = { ...userProfile, avatar: avatarUrl };
      setUserProfile(updatedProfile);

      const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedCache = {
        ...cachedUser,
        avatar: avatarUrl,
        profile: { ...(cachedUser.profile || {}), avatar: avatarUrl },
      };
      localStorage.setItem('user', JSON.stringify(updatedCache));

      setAvatarPreview(avatarUrl);
      setSelectedAvatarFile(null);
      // Notify Navbar/AuthContext so avatar updates live
      window.dispatchEvent(new Event('userUpdated'));
      setToast({ message: 'Profile picture updated successfully!', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update profile picture. Please try again.', type: 'error' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Wallet Functions
  const fetchWalletBalance = async (showNotification = false) => {
    try {
      const response = await walletAPI.getBalance();

      const wbPayload = response?.data || response;
      if (wbPayload) {
        const balanceData = wbPayload.balance !== undefined ? wbPayload : (wbPayload.data || wbPayload);

        const newBalance = {
          balance: balanceData.balance || 0,
          heldBalance: balanceData.held_balance || balanceData.heldBalance || 0,
          currency: balanceData.currency || 'NGN'
        };

        setWalletBalance(newBalance);

        if (showNotification) {
          setToast({ message: '✓ Balance refreshed', type: 'success' });
        }
      }
    } catch (error) {
      setToast({ message: 'Failed to load balance', type: 'error' });
    }
  };

  const fetchWalletTransactions = async () => {
    setIsLoadingTransactions(true);
    try {
      const response = await walletAPI.getTransactions({ limit: 20 });

      const txPayload = response?.data || response;
      if (txPayload) {
        let transactions = [];
        if (Array.isArray(txPayload)) {
          transactions = txPayload;
        } else if (Array.isArray(txPayload.transactions)) {
          transactions = txPayload.transactions;
        } else if (txPayload.data && Array.isArray(txPayload.data)) {
          transactions = txPayload.data;
        }

        setWalletTransactions(transactions);
      } else {
        setWalletTransactions([]);
      }
    } catch (error) {
      setToast({ message: 'Failed to load transactions', type: 'error' });
      setWalletTransactions([]); // Set empty array on error
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  const handleWithdrawalSubmit = async (e) => {
    e.preventDefault();

    // Validate amount
    const amount = parseFloat(withdrawalForm.amount);
    if (isNaN(amount) || amount < 1000) {
      setToast({ message: 'Minimum withdrawal amount is ₦1,000', type: 'error' });
      return;
    }

    if (amount > walletBalance.balance) {
      setToast({ message: 'Insufficient wallet balance', type: 'error' });
      return;
    }

    // bank_code is required by the backend to initiate the disbursement, and
    // only a saved bank account carries one — a manually typed bank name has
    // no code, so the request would be rejected before reaching the gateway.
    const selectedAccount = userBankAccounts.find(
      acc => (acc.id || acc._id) === withdrawalForm.selectedBankId
    );
    if (!selectedAccount?.bank_code) {
      setToast({
        message: 'Select a saved bank account to withdraw to. Add one under Bank Accounts if you have none.',
        type: 'error'
      });
      return;
    }

    setIsProcessingWithdrawal(true);
    try {
      const response = await walletAPI.requestWithdrawal({
        amount: amount,
        bank_account: withdrawalForm.bankAccount,
        bank_name: withdrawalForm.bankName,
        bank_code: selectedAccount.bank_code,
        account_name: withdrawalForm.accountName
      });

      if (response) {
        setToast({ message: '✓ Withdrawal request submitted successfully!', type: 'success' });
        setShowWithdrawModal(false);
        setWithdrawalForm({
          amount: '',
          bankAccount: '',
          bankName: '',
          accountName: ''
        });

        // Refresh wallet balance
        const balanceResponse = await walletAPI.getBalance();
        const refreshedBal = balanceResponse?.data || balanceResponse;
        if (refreshedBal) {
          setWalletBalance({
            balance: refreshedBal.balance || 0,
            heldBalance: refreshedBal.held_balance || 0,
            currency: refreshedBal.currency || 'NGN'
          });
        }
      }
    } catch (error) {
      setToast({ message: 'Failed to process withdrawal. Please try again.', type: 'error' });
    } finally {
      setIsProcessingWithdrawal(false);
    }
  };

  const handleWalletTopup = async () => {
    const amount = parseFloat(topupAmount);

    if (!amount || amount < 100) {
      setToast({ message: 'Minimum top-up amount is ₦100', type: 'error' });
      return;
    }

    try {
      setIsProcessingTopup(true);

      const { paymentAPI } = await import('../services/api');

      const paymentData = {
        amount: parseInt(amount),
        payment_type: 'wallet_topup',
        currency: 'NGN',
        callback_url: `${window.location.origin}/seller-dashboard?payment=success`,
        metadata: {
          topup_amount: parseInt(amount)
        }
      };

      const paymentResponse = await paymentAPI.initializePayment(paymentData);

      if (paymentResponse && paymentResponse.data && paymentResponse.data.checkout_url) {
        localStorage.setItem('pending_payment_id', paymentResponse.data.payment_id);
        localStorage.setItem('pending_payment_reference', paymentResponse.data.reference);
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        setToast({ message: 'Failed to initialize payment', type: 'error' });
      }
    } catch (error) {
      setToast({ message: error.message || 'Failed to process top-up', type: 'error' });
    } finally {
      setIsProcessingTopup(false);
    }
  };

  const handleWithdrawalFormChange = (e) => {
    const { name, value } = e.target;
    setWithdrawalForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch user's saved bank accounts
  const fetchUserBankAccounts = async () => {
    try {
      setLoadingBankAccounts(true);
      const response = await userAPI.getBankAccounts();
      const data = response.data || response;
      setUserBankAccounts(data.bank_accounts || []);
    } catch (error) {
      setUserBankAccounts([]);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  // Handle bank account selection from dropdown
  const handleBankAccountSelect = (e) => {
    const selectedId = e.target.value;
    const selectedAccount = userBankAccounts.find(acc => (acc.id || acc._id) === selectedId);

    if (selectedAccount) {
      setWithdrawalForm(prev => ({
        ...prev,
        selectedBankId: selectedId,
        bankAccount: selectedAccount.account_number,
        bankName: selectedAccount.bank_name,
        accountName: selectedAccount.account_name
      }));
    } else {
      // Manual entry
      setWithdrawalForm(prev => ({
        ...prev,
        selectedBankId: '',
        bankAccount: '',
        bankName: '',
        accountName: ''
      }));
    }
  };

  // Order action state
  const [processingOrderAction, setProcessingOrderAction] = useState(null); // orderId + action

  const handleShipOrder = async (orderId) => {
    const trackingNumber = window.prompt('Enter tracking number (optional):') ?? '';
    setProcessingOrderAction(`${orderId}-ship`);
    try {
      await sellerAPI.shipOrder(orderId, { tracking_number: trackingNumber });
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId) ? { ...o, status: 'shipped' } : o));
      setToast({ message: 'Order marked as shipped', type: 'success' });
    } catch {
      setToast({ message: 'Failed to update order status', type: 'error' });
    } finally {
      setProcessingOrderAction(null);
    }
  };

  const handleMarkDelivered = async (orderId) => {
    if (!window.confirm('Mark this order as delivered?')) return;
    setProcessingOrderAction(`${orderId}-deliver`);
    try {
      await sellerAPI.markOrderDelivered(orderId);
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId) ? { ...o, status: 'delivered' } : o));
      setToast({ message: 'Order marked as delivered', type: 'success' });
    } catch {
      setToast({ message: 'Failed to update order status', type: 'error' });
    } finally {
      setProcessingOrderAction(null);
    }
  };

  const handleRejectOrder = async (orderId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    setProcessingOrderAction(`${orderId}-reject`);
    try {
      await sellerAPI.rejectOrder(orderId, reason);
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId) ? { ...o, status: 'cancelled' } : o));
      setToast({ message: 'Order rejected', type: 'success' });
    } catch {
      setToast({ message: 'Failed to reject order', type: 'error' });
    } finally {
      setProcessingOrderAction(null);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
        }
      },
      y: {
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: isDarkMode ? '#9CA3AF' : '#6B7280',
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: isDarkMode ? '#F3F4F6' : '#1F2937',
          padding: 20,
          usePointStyle: true,
        }
      }
    }
  };

  const renderDashboardContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* KYC Status Banner */}
      <KYCStatusBanner onNavigateToKYC={() => setActiveSection('kyc')} />

      {/* Quick Actions */}
      <div className="AutoboyExpress-dash-quick-actions">
        <button className="AutoboyExpress-dash-quick-action" onClick={() => setActiveSection('products')}>
          <div className="AutoboyExpress-dash-quick-action-icon">
            <FontAwesomeIcon icon={faPlus} />
          </div>
          <div>
            <h3>Add Product</h3>
            <p>List a new product</p>
          </div>
        </button>
        <button className="AutoboyExpress-dash-quick-action" onClick={() => setActiveSection('orders')}>
          <div className="AutoboyExpress-dash-quick-action-icon">
            <FontAwesomeIcon icon={faTruck} />
          </div>
          <div>
            <h3>Manage Orders</h3>
            <p>Process orders</p>
          </div>
        </button>
        <button className="AutoboyExpress-dash-quick-action" onClick={() => setActiveSection('analytics')}>
          <div className="AutoboyExpress-dash-quick-action-icon">
            <FontAwesomeIcon icon={faChartPie} />
          </div>
          <div>
            <h3>View Analytics</h3>
            <p>Sales insights</p>
          </div>
        </button>
        <button
          className="AutoboyExpress-dash-quick-action AutoboyExpress-premium-action"
          onClick={() => setShowPremiumUpgrade(true)}
          style={{
            background: 'linear-gradient(135deg, #22C55E, #16A34A)',
            border: '2px solid #FFD700',
            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.4)'
          }}
        >
          <div className="AutoboyExpress-dash-quick-action-icon" style={{ color: '#FFD700' }}>
            <FontAwesomeIcon icon={faCrown} />
          </div>
          <div>
            <h3 style={{ color: 'white' }}>Upgrade to Premium</h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Unlock advanced features</p>
          </div>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="AutoboyExpress-dash-stats">
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faMobileAlt} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingStats ? <FontAwesomeIcon icon={faSpinner} spin /> : stats.activeProducts}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Active Products</div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faShoppingCart} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingStats ? <FontAwesomeIcon icon={faSpinner} spin /> : stats.pendingOrders}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Pending Orders</div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingStats ? <FontAwesomeIcon icon={faSpinner} spin /> : stats.totalSales}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Completed Sales</div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faNairaSign} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingStats ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(stats.totalEarnings)}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Total Revenue</div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faWallet} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingStats ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(walletBalance.balance)}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Wallet Balance</div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon" style={{ color: performanceMetrics.growthRate >= 0 ? '#22C55E' : '#EF4444' }}>
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value" style={{ color: performanceMetrics.growthRate >= 0 ? '#22C55E' : '#EF4444' }}>
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : `${performanceMetrics.growthRate >= 0 ? '+' : ''}${performanceMetrics.growthRate.toFixed(1)}%`}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Growth Rate</div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faStar} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value" style={{ color: '#F59E0B' }}>
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : performanceMetrics.sellerRating > 0 ? `${performanceMetrics.sellerRating.toFixed(1)}/5` : '—'}
          </div>
          <div className="AutoboyExpress-dash-stat-label">
            Seller Rating{performanceMetrics.totalRatings > 0 ? ` (${performanceMetrics.totalRatings})` : ''}
          </div>
        </div>

        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header">
            <div className="AutoboyExpress-dash-stat-icon">
              <FontAwesomeIcon icon={faChartBar} />
            </div>
          </div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(performanceMetrics.averageOrderValue)}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Avg Order Value</div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="AutoboyExpress-dash-content">
        {/* Recent Orders */}
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Recent Orders</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {orders.slice(0, 3).map(order => (
              <div key={order.id} className="AutoboyExpress-dash-order-item">
                <div className="AutoboyExpress-dash-order-info">
                  <h4>{order.product}</h4>
                  <p>{order.customer}</p>
                  <span className="AutoboyExpress-dash-order-id">{order.id}</span>
                </div>
                <div className="AutoboyExpress-dash-order-details">
                  <div className="AutoboyExpress-dash-order-amount">{formatCurrency(order.amount)}</div>
                  <div
                    className="AutoboyExpress-dash-order-status"
                    style={{ color: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sales Overview */}
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Sales Overview</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div className="AutoboyExpress-dash-chart-container">
              <Line data={salesChartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddProductForm = () => (
    <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowAddProductForm(false)}>
      <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="AutoboyExpress-dash-modal-header">
          <h2>Add New Product</h2>
          <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowAddProductForm(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmitProduct} className="AutoboyExpress-dash-product-form">
          {/* Product Images */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Product Images (Max 10)</h3>
            <div className="AutoboyExpress-dash-image-upload-area">
              <input
                type="file"
                id="product-images"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploadingImages || productImages.length >= 10}
              />
              <label
                htmlFor="product-images"
                className="AutoboyExpress-dash-upload-label"
                style={{
                  opacity: uploadingImages || productImages.length >= 10 ? 0.6 : 1,
                  cursor: uploadingImages || productImages.length >= 10 ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={uploadingImages ? faSpinner : faUpload} spin={uploadingImages} />
                <span>
                  {uploadingImages
                    ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                    : productImages.length >= 10
                      ? 'Maximum images reached (10/10)'
                      : 'Click to upload images from your device'}
                </span>
                {uploadingImages && uploadProgress.fileName && (
                  <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                    {uploadProgress.fileName}
                  </small>
                )}
              </label>
            </div>

            {/* Manual Image URL Input */}
            <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
              <label>Or paste image URL directly:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImageUrl())}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleAddImageUrl}
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                  style={{ padding: '0.5rem 1rem' }}
                  disabled={productImages.length >= 10}
                >
                  Add URL
                </button>
              </div>
              <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                Use direct image URLs from Imgur, Cloudinary, or any image hosting service
              </small>
            </div>

            {productImages.length > 0 && (
              <div className="AutoboyExpress-dash-image-preview-grid">
                {productImages.map((img, index) => (
                  <div key={index} className="AutoboyExpress-dash-image-preview">
                    <img src={img} alt={`Product ${index + 1}`} />
                    <button
                      type="button"
                      className="AutoboyExpress-dash-remove-image"
                      onClick={() => removeImage(index)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Basic Information</h3>
            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Product Title *</label>
                <input
                  type="text"
                  name="title"
                  value={newProduct.title}
                  onChange={handleProductInputChange}
                  placeholder="e.g., iPhone 15 Pro Max 256GB"
                  required
                />
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={newProduct.category}
                  onChange={handleProductInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.length > 0 ? (
                    categories.map(cat => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="smartphones">Smartphones</option>
                      <option value="laptops">Laptops</option>
                      <option value="tablets">Tablets</option>
                      <option value="smartwatches">Smartwatches</option>
                      <option value="headphones">Headphones</option>
                      <option value="gaming_consoles">Gaming Consoles</option>
                      <option value="cameras">Cameras</option>
                      <option value="audio_equipment">Audio Equipment</option>
                      <option value="accessories">Accessories</option>
                    </>
                  )}
                </select>
                {categories.length === 0 && (
                  <small style={{ color: '#ff6b6b', fontSize: '0.85rem' }}>
                    Loading categories... If categories don't load, contact support.
                  </small>
                )}
                {newProduct.category === 'accessories' && JSON.parse(localStorage.getItem('user') || '{}').user_type !== 'admin' && (
                  <div style={{
                    color: '#ef4444', fontSize: '0.85rem', marginTop: '4px',
                    padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }} />
                    Only admins can post in the Accessories category. Please select a different category.
                  </div>
                )}
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Condition *</label>
                <select
                  name="condition"
                  value={newProduct.condition}
                  onChange={handleProductInputChange}
                  required
                >
                  <option value="new">Brand New</option>
                  <option value="uk_used">UK Used</option>
                  <option value="nigeria_used">Nigerian Used</option>
                  <option value="swap_deal">Swap Deal</option>
                  <option value="for_sale">For Sale</option>
                </select>
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Brand</label>
                <input
                  type="text"
                  name="brand"
                  value={newProduct.brand}
                  onChange={handleProductInputChange}
                  placeholder="e.g., Apple, Samsung"
                />
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Model</label>
                <input
                  type="text"
                  name="model"
                  value={newProduct.model}
                  onChange={handleProductInputChange}
                  placeholder="e.g., iPhone 15 Pro Max"
                />
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Colours available</label>
                <ColorMultiSelect
                  value={newProduct.colors}
                  onChange={(colors) => setNewProduct(prev => ({ ...prev, colors, color: colors[0] || '' }))}
                  availableColors={availableColors}
                  loading={loadingColors}
                  modelEntered={!!newProduct.model}
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>
          </div>

          {/* Pricing & Stock */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Pricing & Stock</h3>
            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Price (₦) *</label>
                <input
                  type="number"
                  name="price"
                  value={newProduct.price}
                  onChange={handleProductInputChange}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
                <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                  Regular price shown to buyers
                </small>
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Seller Price (₦) - Optional</label>
                <input
                  type="number"
                  name="sellerPrice"
                  value={newProduct.sellerPrice}
                  onChange={handleProductInputChange}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
                <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                  Wholesale price for other sellers (must be less than regular price)
                </small>
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Quantity in Stock *</label>
                <input
                  type="number"
                  name="quantity"
                  value={newProduct.quantity}
                  onChange={handleProductInputChange}
                  placeholder="0"
                  min="1"
                  required
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Product Description</h3>
            <div className="AutoboyExpress-dash-form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={newProduct.description}
                onChange={handleProductInputChange}
                rows="5"
                placeholder="Describe your product in detail..."
                required
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label>Specifications</label>
              <textarea
                name="specifications"
                value={newProduct.specifications}
                onChange={handleProductInputChange}
                rows="4"
                placeholder="Storage: 256GB, RAM: 8GB, Color: Space Black..."
              />
            </div>
          </div>

          {/* Location & Additional */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Location & Additional Info</h3>
            <div className="AutoboyExpress-dash-form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={newProduct.location}
                onChange={handleProductInputChange}
                placeholder="e.g., Lagos, Ikeja"
                required
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                name="tags"
                value={newProduct.tags}
                onChange={handleProductInputChange}
                placeholder="e.g., smartphone, apple, ios"
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label className="AutoboyExpress-dash-checkbox-label">
                <input
                  type="checkbox"
                  name="swapAvailable"
                  checked={newProduct.swapAvailable}
                  onChange={handleProductInputChange}
                />
                <span>Available for Swap/Exchange</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="AutoboyExpress-dash-form-actions">
            <button
              type="button"
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
              onClick={() => setShowAddProductForm(false)}
            >
              Cancel
            </button>
            <button type="submit" className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary">
              <FontAwesomeIcon icon={faSave} />
              Add Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderViewProductModal = () => (
    <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowViewProductModal(false)}>
      <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="AutoboyExpress-dash-modal-header">
          <h2>Product Details</h2>
          <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowViewProductModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {selectedProduct && (
          <div className="AutoboyExpress-dash-product-details">
            {/* Product Images */}
            {selectedProduct.images && selectedProduct.images.length > 0 && (
              <div className="AutoboyExpress-dash-form-section">
                <h3>Product Images</h3>
                <div className="AutoboyExpress-dash-image-preview-grid">
                  {selectedProduct.images.map((img, index) => (
                    <div key={index} className="AutoboyExpress-dash-image-preview">
                      <img src={typeof img === 'string' ? img : img.url} alt={`Product ${index + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="AutoboyExpress-dash-form-section">
              <h3>Basic Information</h3>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Title:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.name || selectedProduct.title}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Category:</span>
                {/* The product carries category_id, not a name — resolve it
                    against the category list this dashboard already loads,
                    otherwise this row always read "N/A". */}
                <span className="AutoboyExpress-dash-detail-value">
                  {categories.find(cat => (cat._id || cat.id) === (selectedProduct.category_id || selectedProduct.category))?.name
                    || (typeof selectedProduct.category === 'string' && !/^[a-f0-9]{24}$/i.test(selectedProduct.category) ? selectedProduct.category : null)
                    || 'N/A'}
                </span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Brand:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.brand || 'N/A'}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Model:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.model || 'N/A'}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Colour(s):</span>
                <span className="AutoboyExpress-dash-detail-value">
                  {selectedProduct.colors?.length
                    ? selectedProduct.colors.join(', ')
                    : (selectedProduct.color || 'N/A')}
                </span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Condition:</span>
                <span className="AutoboyExpress-dash-detail-value" style={{ textTransform: 'capitalize' }}>
                  {(selectedProduct.condition || 'N/A').toString().replace(/_/g, ' ')}
                </span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Price:</span>
                <span className="AutoboyExpress-dash-detail-value">₦{(selectedProduct.price || 0).toLocaleString()}</span>
              </div>
              {(selectedProduct.seller_price || selectedProduct.sellerPrice) ? (
                <div className="AutoboyExpress-dash-detail-row">
                  <span className="AutoboyExpress-dash-detail-label">Wholesale Price:</span>
                  <span className="AutoboyExpress-dash-detail-value">
                    ₦{Number(selectedProduct.seller_price || selectedProduct.sellerPrice).toLocaleString()}
                  </span>
                </div>
              ) : null}
              {selectedProduct.is_flash_deal && selectedProduct.flash_deal_price ? (
                <div className="AutoboyExpress-dash-detail-row">
                  <span className="AutoboyExpress-dash-detail-label">Flash Deal Price:</span>
                  <span className="AutoboyExpress-dash-detail-value" style={{ color: '#EA580C', fontWeight: 700 }}>
                    ₦{Number(selectedProduct.flash_deal_price).toLocaleString()}
                    {selectedProduct.flash_deal_ends_at ? ` · ends ${new Date(selectedProduct.flash_deal_ends_at).toLocaleString()}` : ''}
                  </span>
                </div>
              ) : null}
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Stock:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.stock ?? selectedProduct.quantity ?? 0}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Sold:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.sold_count ?? selectedProduct.sold ?? 0}</span>
              </div>
              {selectedProduct.sku && (
                <div className="AutoboyExpress-dash-detail-row">
                  <span className="AutoboyExpress-dash-detail-label">SKU:</span>
                  <span className="AutoboyExpress-dash-detail-value">{selectedProduct.sku}</span>
                </div>
              )}
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Status:</span>
                <span className="AutoboyExpress-dash-detail-value" style={{ textTransform: 'capitalize' }}>
                  {selectedProduct.status || 'Active'}
                  {selectedProduct.status === 'draft' ? ' — awaiting admin approval' : ''}
                </span>
              </div>
            </div>

            {/* Performance — the numbers a seller actually wants when judging
                whether a listing is working. Previously not shown at all. */}
            <div className="AutoboyExpress-dash-form-section">
              <h3>Performance</h3>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Views:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.view_count ?? 0}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Wishlisted:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.wishlist_count ?? 0}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Likes:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.like_count ?? 0}</span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Rating:</span>
                <span className="AutoboyExpress-dash-detail-value">
                  {selectedProduct.review_count
                    ? `${(selectedProduct.average_rating || 0).toFixed(1)} ★ (${selectedProduct.review_count} review${selectedProduct.review_count > 1 ? 's' : ''})`
                    : 'No reviews yet'}
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="AutoboyExpress-dash-form-section">
              <h3>Description</h3>
              <p className="AutoboyExpress-dash-detail-description">{selectedProduct.description || 'No description provided'}</p>
            </div>

            {/* Specifications */}
            {selectedProduct.specifications && (
              <div className="AutoboyExpress-dash-form-section">
                <h3>Specifications</h3>
                <p className="AutoboyExpress-dash-detail-description">
                  {typeof selectedProduct.specifications === 'string'
                    ? selectedProduct.specifications
                    : selectedProduct.specifications.details || JSON.stringify(selectedProduct.specifications, null, 2)}
                </p>
              </div>
            )}

            {/* Location & Additional */}
            <div className="AutoboyExpress-dash-form-section">
              <h3>Location & Additional Info</h3>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Location:</span>
                <span className="AutoboyExpress-dash-detail-value">
                  {selectedProduct.location?.city || selectedProduct.location || 'N/A'}
                  {selectedProduct.location?.state && `, ${selectedProduct.location.state}`}
                </span>
              </div>
              {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                <div className="AutoboyExpress-dash-detail-row">
                  <span className="AutoboyExpress-dash-detail-label">Tags:</span>
                  <span className="AutoboyExpress-dash-detail-value">{selectedProduct.tags.join(', ')}</span>
                </div>
              )}
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Swap Available:</span>
                <span className="AutoboyExpress-dash-detail-value">{selectedProduct.swap_available ? 'Yes' : 'No'}</span>
              </div>
              {(selectedProduct.is_featured || selectedProduct.is_trending || selectedProduct.is_hot || selectedProduct.boost_level > 0) && (
                <div className="AutoboyExpress-dash-detail-row">
                  <span className="AutoboyExpress-dash-detail-label">Promotions:</span>
                  <span className="AutoboyExpress-dash-detail-value">
                    {[
                      selectedProduct.is_featured && 'Good Deal',
                      selectedProduct.is_trending && 'Trending',
                      selectedProduct.is_hot && 'Hot Deal',
                      selectedProduct.boost_level > 0 && `Boosted ×${selectedProduct.boost_level}`,
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>

            {/* Timestamps */}
            <div className="AutoboyExpress-dash-form-section">
              <h3>Timestamps</h3>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Created:</span>
                <span className="AutoboyExpress-dash-detail-value">
                  {selectedProduct.created_at ? new Date(selectedProduct.created_at).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div className="AutoboyExpress-dash-detail-row">
                <span className="AutoboyExpress-dash-detail-label">Last Updated:</span>
                <span className="AutoboyExpress-dash-detail-value">
                  {selectedProduct.updated_at ? new Date(selectedProduct.updated_at).toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="AutoboyExpress-dash-form-actions">
              <button
                type="button"
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                onClick={() => setShowViewProductModal(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                onClick={() => {
                  setShowViewProductModal(false);
                  handleEditProduct(selectedProduct);
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                Edit Product
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderEditProductForm = () => (
    <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowEditProductForm(false)}>
      <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="AutoboyExpress-dash-modal-header">
          <h2>Edit Product</h2>
          <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowEditProductForm(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleUpdateProduct} className="AutoboyExpress-dash-product-form">
          {/* Product Images */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Product Images (Max 10)</h3>
            <div className="AutoboyExpress-dash-image-upload-area">
              <input
                type="file"
                id="edit-product-images"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploadingImages || productImages.length >= 10}
              />
              <label
                htmlFor="edit-product-images"
                className="AutoboyExpress-dash-upload-label"
                style={{
                  opacity: uploadingImages || productImages.length >= 10 ? 0.6 : 1,
                  cursor: uploadingImages || productImages.length >= 10 ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={uploadingImages ? faSpinner : faUpload} spin={uploadingImages} />
                <span>
                  {uploadingImages
                    ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                    : productImages.length >= 10
                      ? 'Maximum images reached (10/10)'
                      : 'Click to upload images from your device'}
                </span>
                {uploadingImages && uploadProgress.fileName && (
                  <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.85rem' }}>
                    {uploadProgress.fileName}
                  </small>
                )}
              </label>
            </div>

            {/* Manual Image URL Input */}
            <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
              <label>Or paste image URL directly:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddImageUrl())}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={handleAddImageUrl}
                  className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                  style={{ padding: '0.5rem 1rem' }}
                  disabled={productImages.length >= 10}
                >
                  Add URL
                </button>
              </div>
              <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                Use direct image URLs from Imgur, Cloudinary, or any image hosting service
              </small>
            </div>

            {productImages.length > 0 && (
              <div className="AutoboyExpress-dash-image-preview-grid">
                {productImages.map((img, index) => (
                  <div key={index} className="AutoboyExpress-dash-image-preview">
                    <img src={img} alt={`Product ${index + 1}`} />
                    <button
                      type="button"
                      className="AutoboyExpress-dash-remove-image"
                      onClick={() => removeImage(index)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Basic Information</h3>
            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Product Title *</label>
                <input
                  type="text"
                  name="title"
                  value={newProduct.title}
                  onChange={handleProductInputChange}
                  placeholder="e.g., iPhone 15 Pro Max 256GB"
                  required
                />
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={newProduct.category}
                  onChange={handleProductInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.length > 0 ? (
                    categories.map(cat => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="smartphones">Smartphones</option>
                      <option value="laptops">Laptops</option>
                      <option value="tablets">Tablets</option>
                      <option value="smartwatches">Smartwatches</option>
                      <option value="headphones">Headphones</option>
                      <option value="gaming_consoles">Gaming Consoles</option>
                      <option value="cameras">Cameras</option>
                      <option value="audio_equipment">Audio Equipment</option>
                      <option value="accessories">Accessories</option>
                    </>
                  )}
                </select>
                {newProduct.category === 'accessories' && JSON.parse(localStorage.getItem('user') || '{}').user_type !== 'admin' && (
                  <div style={{
                    color: '#ef4444', fontSize: '0.85rem', marginTop: '4px',
                    padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }} />
                    Only admins can post in the Accessories category. Please select a different category.
                  </div>
                )}
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Condition *</label>
                <select
                  name="condition"
                  value={newProduct.condition}
                  onChange={handleProductInputChange}
                  required
                >
                  <option value="">Select Condition</option>
                  <option value="new">Brand New</option>
                  <option value="uk_used">UK Used</option>
                  <option value="nigeria_used">Nigerian Used</option>
                  <option value="swap_deal">Swap Deal</option>
                  <option value="for_sale">For Sale</option>
                </select>
              </div>
            </div>

            <div className="AutoboyExpress-dash-form-row">
              <div className="AutoboyExpress-dash-form-group">
                <label>Price (₦) *</label>
                <input
                  type="number"
                  name="price"
                  value={newProduct.price}
                  onChange={handleProductInputChange}
                  placeholder="e.g., 850000"
                  min="0"
                  step="0.01"
                  required
                />
              </div>

              <div className="AutoboyExpress-dash-form-group">
                <label>Stock Quantity *</label>
                <input
                  type="number"
                  name="quantity"
                  value={newProduct.quantity}
                  onChange={handleProductInputChange}
                  placeholder="e.g., 10"
                  min="0"
                  required
                />
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Product Details</h3>
            <div className="AutoboyExpress-dash-form-group">
              <label>Description *</label>
              <textarea
                name="description"
                value={newProduct.description}
                onChange={handleProductInputChange}
                rows="5"
                placeholder="Describe your product in detail..."
                required
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label>Specifications</label>
              <textarea
                name="specifications"
                value={newProduct.specifications}
                onChange={handleProductInputChange}
                rows="4"
                placeholder="Storage: 256GB, RAM: 8GB, Color: Space Black..."
              />
            </div>
          </div>

          {/* Location & Additional */}
          <div className="AutoboyExpress-dash-form-section">
            <h3>Location & Additional Info</h3>
            <div className="AutoboyExpress-dash-form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={newProduct.location}
                onChange={handleProductInputChange}
                placeholder="e.g., Lagos, Ikeja"
                required
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                name="tags"
                value={newProduct.tags}
                onChange={handleProductInputChange}
                placeholder="e.g., smartphone, apple, ios"
              />
            </div>

            <div className="AutoboyExpress-dash-form-group">
              <label className="AutoboyExpress-dash-checkbox-label">
                <input
                  type="checkbox"
                  name="swapAvailable"
                  checked={newProduct.swapAvailable}
                  onChange={handleProductInputChange}
                />
                <span>Available for Swap/Exchange</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="AutoboyExpress-dash-form-actions">
            <button
              type="button"
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
              onClick={() => setShowEditProductForm(false)}
            >
              Cancel
            </button>
            <button type="submit" className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary">
              <FontAwesomeIcon icon={faSave} />
              Update Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderProductsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">My Products</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* View Toggle */}
            <div className="AutoboyExpress-view-toggle">
              <button
                className={`AutoboyExpress-view-btn ${productsViewMode === 'list' ? 'active' : ''}`}
                onClick={() => setProductsViewMode('list')}
                title="List View"
              >
                <FontAwesomeIcon icon={faList} />
              </button>
              <button
                className={`AutoboyExpress-view-btn ${productsViewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setProductsViewMode('grid')}
                title="Grid View"
              >
                <FontAwesomeIcon icon={faThLarge} />
              </button>
            </div>
            <button
              className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
              onClick={() => setShowAddProductForm(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Product
            </button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedProducts.length > 0 && (
          <div style={{
            background: isDarkMode ? '#1e293b' : '#f0fdf4',
            borderBottom: '1px solid ' + (isDarkMode ? '#334155' : '#bbf7d0'),
            padding: '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 50,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ fontWeight: 600, color: isDarkMode ? '#f8fafc' : '#166534' }}>
                {selectedProducts.length} items selected
              </span>
              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                onClick={() => setSelectedProducts([])}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
              >
                Clear Selection
              </button>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <select
                onChange={(e) => handleBulkStatusUpdate(e.target.value)}
                value=""
                className="AutoboyExpress-dash-select"
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', width: 'auto' }}
                disabled={isProcessingBulkAction}
              >
                <option value="" disabled>Update Status...</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="sold">Mark as Sold</option>
              </select>

              <button
                className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-danger"
                onClick={handleBulkDelete}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
                disabled={isProcessingBulkAction}
              >
                <FontAwesomeIcon icon={faTrashAlt} style={{ marginRight: '0.4rem' }} />
                Delete Selected
              </button>
            </div>
          </div>
        )}

        <div className="AutoboyExpress-dash-section-content">
          {productsViewMode === 'list' ? (
            // List View (Modern Cards)
            <div className="AutoboyExpress-modern-list-container">
              {isLoadingProducts ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>
                  Loading products...
                </div>
              ) : products.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#9CA3AF' }}>
                  No products found. Click "Add Product" to create your first listing.
                </div>
              ) : products.map(product => (
                <div key={product.id || product._id} className="AutoboyExpress-modern-list-item">
                  <div style={{ padding: '0 1rem', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id || product._id)}
                      onChange={() => toggleProductSelection(product.id || product._id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#22c55e' }}
                    />
                  </div>
                  {/* Product Image Section */}
                  <div className="AutoboyExpress-list-image-section">
                    <div className="AutoboyExpress-list-image-wrapper">
                      <img
                        src={product.image || 'https://via.placeholder.com/120x120?text=No+Image'}
                        alt={product.name}
                        className="AutoboyExpress-list-image"
                      />
                    </div>
                    <div className="AutoboyExpress-list-status-badge" data-status={product.status}>
                      <span className="AutoboyExpress-status-dot"></span>
                      {getStatusText(product.status)}
                    </div>
                  </div>

                  {/* Product Info Section */}
                  <div className="AutoboyExpress-list-info-section">
                    <h3 className="AutoboyExpress-list-product-name">{product.name}</h3>
                    <div className="AutoboyExpress-list-product-price">{formatCurrency(product.price)}</div>
                  </div>

                  {/* Metrics Section */}
                  <div className="AutoboyExpress-list-metrics-section">
                    <div className="AutoboyExpress-list-metric">
                      <span className="AutoboyExpress-list-metric-label">Stock</span>
                      <span className="AutoboyExpress-list-metric-value">{product.stock || 0}</span>
                    </div>
                    <div className="AutoboyExpress-list-metric">
                      <span className="AutoboyExpress-list-metric-label">Sales</span>
                      <span className="AutoboyExpress-list-metric-value">{product.sold || 0}</span>
                    </div>
                    <div className="AutoboyExpress-list-metric">
                      <span className="AutoboyExpress-list-metric-label">Revenue</span>
                      <span className="AutoboyExpress-list-metric-value">
                        ₦{((product.price * (product.sold || 0)) / 1000).toFixed(0)}k
                      </span>
                    </div>
                  </div>

                  {/* Actions Section */}
                  <div className="AutoboyExpress-list-actions-section">
                    <button
                      className="AutoboyExpress-list-action-btn AutoboyExpress-list-btn-view"
                      onClick={() => handleViewProduct(product)}
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      <span>View</span>
                    </button>
                    <button
                      className="AutoboyExpress-list-action-btn AutoboyExpress-list-btn-edit"
                      onClick={() => handleEditProduct(product)}
                      title="Edit Product"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      <span>Edit</span>
                    </button>
                    <button
                      className="AutoboyExpress-list-action-btn AutoboyExpress-list-btn-delete"
                      onClick={() => handleDeleteProduct(product)}
                      title="Delete Product"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Grid View (Cards)
            <div className="AutoboyExpress-dash-products-grid">
              {isLoadingProducts ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                  Loading products...
                </div>
              ) : products.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                  No products found. Click "Add Product" to create your first listing.
                </div>
              ) : products.map(product => (
                <div key={product.id || product._id} className="AutoboyExpress-modern-product-card">
                  {/* Selection Checkbox */}
                  <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 10, background: 'rgba(255,255,255,0.8)', padding: '4px', borderRadius: '4px' }}>
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id || product._id)}
                      onChange={() => toggleProductSelection(product.id || product._id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#22c55e' }}
                    />
                  </div>
                  {/* Status Badge */}
                  <div className="AutoboyExpress-product-status-badge" data-status={product.status}>
                    <span className="AutoboyExpress-status-dot"></span>
                    {getStatusText(product.status)}
                  </div>

                  {/* Product Image */}
                  <div className="AutoboyExpress-product-image-wrapper">
                    <img
                      src={product.image || 'https://via.placeholder.com/300x300?text=No+Image'}
                      alt={product.name}
                      className="AutoboyExpress-product-image"
                    />
                    <div className="AutoboyExpress-product-overlay">
                      <button
                        className="AutoboyExpress-product-quick-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewProduct(product);
                        }}
                        title="Quick View"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="AutoboyExpress-product-info">
                    <h3 className="AutoboyExpress-product-name">{product.name}</h3>

                    {/* Price and Stats */}
                    <div className="AutoboyExpress-product-price-section">
                      <div className="AutoboyExpress-product-price">
                        <span className="AutoboyExpress-price-label">Price</span>
                        <span className="AutoboyExpress-price-value">{formatCurrency(product.price)}</span>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="AutoboyExpress-product-metrics">
                      <div className="AutoboyExpress-metric">
                        <span className="AutoboyExpress-metric-label">Stock</span>
                        <span className="AutoboyExpress-metric-value">{product.stock}</span>
                      </div>
                      <div className="AutoboyExpress-metric-divider"></div>
                      <div className="AutoboyExpress-metric">
                        <span className="AutoboyExpress-metric-label">Sales</span>
                        <span className="AutoboyExpress-metric-value">{product.sold || 0}</span>
                      </div>
                      <div className="AutoboyExpress-metric-divider"></div>
                      <div className="AutoboyExpress-metric">
                        <span className="AutoboyExpress-metric-label">Revenue</span>
                        <span className="AutoboyExpress-metric-value">
                          ₦{((product.price * (product.sold || 0)) / 1000).toFixed(0)}k
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="AutoboyExpress-product-actions">
                      <button
                        className="AutoboyExpress-action-btn AutoboyExpress-btn-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditProduct(product);
                        }}
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        <span>Edit</span>
                      </button>
                      <button
                        className="AutoboyExpress-action-btn AutoboyExpress-btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProduct(product);
                        }}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Shared between the table row's Actions cell and the mobile/tablet card's
  // action row so the two views can never drift out of sync with each other.
  const renderOrderActions = (order) => (
    <>
      <IconBtn onClick={() => navigate(`/order-confirmation/${order._id || order.id}`)} title="View order details">
        <FontAwesomeIcon icon={faEye} />
      </IconBtn>
      {/* IMEI entry — only once the order is actually paid for; nothing to
          record for a still-pending order. compact renders it icon-sized
          (matching the buttons around it) instead of the full text pill,
          which was wider than the whole Actions column on its own. */}
      {order.status !== 'pending' && order.status !== 'cancelled' && (
        <OrderIMEIButton order={order} showCustomToast={setToast} isDarkMode={isDarkMode} compact />
      )}
      {/* Ship — pending/processing orders */}
      {(order.status === 'pending' || order.status === 'processing') && (
        <IconBtn
          title="Mark as shipped"
          disabled={processingOrderAction === `${order.id}-ship`}
          onClick={() => handleShipOrder(order._id || order.id)}
        >
          {processingOrderAction === `${order.id}-ship`
            ? <FontAwesomeIcon icon={faSpinner} spin />
            : <FontAwesomeIcon icon={faTruck} />}
        </IconBtn>
      )}
      {/* Delivered — shipped orders */}
      {order.status === 'shipped' && (
        <IconBtn
          tone="green"
          title="Mark as delivered"
          disabled={processingOrderAction === `${order.id}-deliver`}
          onClick={() => handleMarkDelivered(order._id || order.id)}
        >
          {processingOrderAction === `${order.id}-deliver`
            ? <FontAwesomeIcon icon={faSpinner} spin />
            : <FontAwesomeIcon icon={faCheckCircle} />}
        </IconBtn>
      )}
      {/* Reject — pending/processing orders only */}
      {(order.status === 'pending' || order.status === 'processing') && (
        <IconBtn
          tone="red"
          title="Reject order"
          disabled={processingOrderAction === `${order.id}-reject`}
          onClick={() => handleRejectOrder(order._id || order.id)}
        >
          {processingOrderAction === `${order.id}-reject`
            ? <FontAwesomeIcon icon={faSpinner} spin />
            : <FontAwesomeIcon icon={faBan} />}
        </IconBtn>
      )}
      {order.use_escrow && (
        <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-500 tw-self-center" title="Protected by escrow" />
      )}
    </>
  );

  const renderOrdersContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">Order Management</h2>
          <div className="AutoboyExpress-dash-filters">
            <button className="AutoboyExpress-dash-filter-btn">
              <FontAwesomeIcon icon={faFilter} />
              Filter
            </button>
            <div className="AutoboyExpress-dash-search">
              <FontAwesomeIcon icon={faSearch} />
              <input type="text" placeholder="Search orders..." />
            </div>
          </div>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          {/* Table — small-PC and up. Below that, a 1000px-min-width table
              inside a horizontally-scrolling wrapper was the only way to see
              this data at all; it's replaced below by a stacked card per
              order that needs no horizontal scroll. */}
          <div className="tw-hidden lg:tw-block">
            <TableCard>
              <DataTable
                // Order ID is a raw Mongo id (24 unbroken hex characters) —
                // at the old 10% it regularly ran past its own column into
                // Customer, since a string with no spaces has nowhere to
                // wrap and (correctly, post the Td overflow-hidden fix) is
                // no longer silently clipped either. Actions gave the room:
                // every action in it is now a fixed 32px icon rather than
                // the old text pill, so it never needed 20%.
                columns={[{ width: '16%' }, { width: '16%' }, { width: '14%' }, { width: '10%' }, { width: '9%' }, { width: '10%' }, { width: '10%' }, { width: '15%' }]}
                minWidth="1150px"
              >
                <THead>
                  <Th>Order ID</Th>
                  <Th>Customer</Th>
                  <Th>Products (Qty)</Th>
                  <Th align="right">Amount</Th>
                  <Th>Date</Th>
                  <Th>Order Status</Th>
                  <Th>Payment</Th>
                  <Th align="center">Actions</Th>
                </THead>
                <tbody>
                  {isLoadingOrders ? (
                    <TableLoading colSpan={8} />
                  ) : orders.length === 0 ? (
                    <TableEmpty colSpan={8} message="No orders yet — orders appear here when customers buy your products" />
                  ) : orders.map(order => (
                    <Tr key={order.id}>
                      <Td className="tw-font-semibold tw-truncate" title={order.id}>{order.id}</Td>
                      <Td>
                        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                          <span className="tw-font-medium tw-truncate tw-flex tw-items-center tw-gap-1.5" title={order.customer}>
                            {order.customer}
                            {order.special_packaging && (
                              <span title="Premium buyer — use premium packaging" className="tw-text-[0.65rem] tw-font-bold tw-text-amber-600 dark:tw-text-amber-400 tw-bg-amber-500/10 tw-border tw-border-amber-500/25 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                                <FontAwesomeIcon icon={faGift} /> Premium Packaging
                              </span>
                            )}
                          </span>
                        </div>
                      </Td>
                      <Td className="tw-truncate" title={order.product}>{order.product}</Td>
                      <Td align="right" className="tw-font-semibold tw-tabular-nums">
                        {formatCurrency(order.amount)}
                        {/* The order total above is what the buyer paid —
                            it includes logistics and the escrow fee, neither
                            of which is the seller's money. This is the real
                            figure, matching the invoice's "Your Payout" and
                            the actual wallet credit on release. */}
                        {typeof order.sellerPayout === 'number' && (
                          <div className="tw-text-xs tw-font-normal tw-text-green-600 dark:tw-text-green-400 tw-mt-0.5">
                            You get: {formatCurrency(order.sellerPayout)}
                          </div>
                        )}
                      </Td>
                      <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">{format(order.date, 'MMM dd, yyyy')}</Td>
                      <Td><Badge tone={statusTone(order.status)}>{getStatusText(order.status)}</Badge></Td>
                      <Td><Badge tone={statusTone(order.payment_status)}>{getPaymentStatusText(order.payment_status, order)}</Badge></Td>
                      <Td align="center">
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-flex-wrap">
                          {renderOrderActions(order)}
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </DataTable>
            </TableCard>
          </div>

          {/* Cards — mobile, tablet, and small-PC widths. */}
          <div className="lg:tw-hidden tw-flex tw-flex-col tw-gap-3">
            {isLoadingOrders ? (
              <div className="tw-flex tw-justify-center tw-py-14">
                <FontAwesomeIcon icon={faSpinner} spin className="tw-text-2xl tw-text-green-500" />
              </div>
            ) : orders.length === 0 ? (
              <div className="tw-text-center tw-py-14 tw-px-4">
                <FontAwesomeIcon icon={faInbox} className="tw-text-3xl tw-mb-3 tw-text-zinc-300 dark:tw-text-zinc-700" />
                <p className="tw-text-sm tw-font-medium tw-text-zinc-400 dark:tw-text-zinc-500 tw-m-0">
                  No orders yet — orders appear here when customers buy your products
                </p>
              </div>
            ) : orders.map(order => (
              <div
                key={order.id}
                className="tw-rounded-2xl tw-border tw-border-black/5 dark:tw-border-white/10 tw-bg-white/95 dark:tw-bg-zinc-900/70 tw-p-4 tw-shadow-[0_2px_10px_rgba(0,0,0,0.04)]"
              >
                <div className="tw-flex tw-justify-between tw-items-start tw-gap-3">
                  <div className="tw-min-w-0">
                    <div className="tw-font-semibold tw-text-sm tw-truncate">{order.id}</div>
                    <div className="tw-text-xs tw-text-zinc-500 dark:tw-text-zinc-400 tw-mt-0.5">{format(order.date, 'MMM dd, yyyy')}</div>
                  </div>
                  <div className="tw-text-right tw-flex-shrink-0">
                    <div className="tw-font-bold tw-text-sm tw-tabular-nums">{formatCurrency(order.amount)}</div>
                    {typeof order.sellerPayout === 'number' && (
                      <div className="tw-text-xs tw-font-medium tw-text-green-600 dark:tw-text-green-400 tw-mt-0.5">
                        You get: {formatCurrency(order.sellerPayout)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="tw-mt-3 tw-min-w-0">
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-font-medium tw-text-sm tw-truncate">
                    <span className="tw-truncate">{order.customer}</span>
                    {order.special_packaging && (
                      <span title="Premium buyer — use premium packaging" className="tw-text-[0.65rem] tw-font-bold tw-text-amber-600 dark:tw-text-amber-400 tw-bg-amber-500/10 tw-border tw-border-amber-500/25 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                        <FontAwesomeIcon icon={faGift} /> Premium
                      </span>
                    )}
                  </div>
                </div>

                <div className="tw-text-sm tw-text-zinc-600 dark:tw-text-zinc-300 tw-mt-2 tw-break-words">{order.product}</div>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-3">
                  <Badge tone={statusTone(order.status)}>{getStatusText(order.status)}</Badge>
                  <Badge tone={statusTone(order.payment_status)}>{getPaymentStatusText(order.payment_status, order)}</Badge>
                  {order.use_escrow && (
                    <FontAwesomeIcon icon={faShieldAlt} className="tw-text-green-500" title="Protected by escrow" />
                  )}
                </div>

                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-1.5 tw-mt-3 tw-pt-3 tw-border-t tw-border-black/5 dark:tw-border-white/10">
                  {renderOrderActions(order)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderEarningsContent = () => {
    const currentMonthEarnings = earningsData.monthlyBreakdown.length > 0
      ? earningsData.monthlyBreakdown[earningsData.monthlyBreakdown.length - 1]?.revenue || 0
      : 0;

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Summary Cards — all real API data */}
        <div className="AutoboyExpress-dash-earnings-cards">
          <div className="AutoboyExpress-dash-earnings-card">
            <div className="AutoboyExpress-dash-earnings-icon"><FontAwesomeIcon icon={faWallet} /></div>
            <div className="AutoboyExpress-dash-earnings-info">
              <h3>Available Balance</h3>
              <div className="AutoboyExpress-dash-earnings-amount">
                {isLoadingEarnings ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(walletBalance.balance)}
              </div>
              <small style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Ready to withdraw</small>
            </div>
          </div>
          <div className="AutoboyExpress-dash-earnings-card">
            <div className="AutoboyExpress-dash-earnings-icon"><FontAwesomeIcon icon={faLock} /></div>
            <div className="AutoboyExpress-dash-earnings-info">
              <h3>Held in Escrow</h3>
              <div className="AutoboyExpress-dash-earnings-amount">
                {isLoadingEarnings ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(walletBalance.heldBalance)}
              </div>
              <small style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Pending buyer confirmation</small>
            </div>
          </div>
          <div className="AutoboyExpress-dash-earnings-card">
            <div className="AutoboyExpress-dash-earnings-icon"><FontAwesomeIcon icon={faChartLine} /></div>
            <div className="AutoboyExpress-dash-earnings-info">
              <h3>This Month</h3>
              <div className="AutoboyExpress-dash-earnings-amount">
                {isLoadingEarnings ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(currentMonthEarnings)}
              </div>
              <small style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Current month revenue</small>
            </div>
          </div>
          <div className="AutoboyExpress-dash-earnings-card">
            <div className="AutoboyExpress-dash-earnings-icon"><FontAwesomeIcon icon={faDownload} /></div>
            <div className="AutoboyExpress-dash-earnings-info">
              <h3>Total Withdrawn</h3>
              <div className="AutoboyExpress-dash-earnings-amount">
                {isLoadingEarnings ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(walletAnalytics.totalWithdrawn)}
              </div>
              <small style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Lifetime withdrawals</small>
            </div>
          </div>
        </div>

        <div className="AutoboyExpress-dash-content">
          {/* Monthly Earnings Chart */}
          <div className="AutoboyExpress-dash-section">
            <div className="AutoboyExpress-dash-section-header">
              <h2 className="AutoboyExpress-dash-section-title">Monthly Earnings</h2>
            </div>
            <div className="AutoboyExpress-dash-section-content">
              {isLoadingEarnings ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                </div>
              ) : earningsData.monthlyBreakdown.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  <FontAwesomeIcon icon={faChartLine} size="2x" style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
                  <p>No monthly data yet</p>
                </div>
              ) : (
                <div className="AutoboyExpress-dash-chart-container">
                  <Bar data={monthlyEarningsData} options={chartOptions} />
                </div>
              )}
            </div>
          </div>

          {/* Earnings Breakdown — all real data, no hardcoded calculations */}
          <div className="AutoboyExpress-dash-section">
            <div className="AutoboyExpress-dash-section-header">
              <h2 className="AutoboyExpress-dash-section-title">Earnings Breakdown</h2>
            </div>
            <div className="AutoboyExpress-dash-section-content">
              {isLoadingEarnings ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
                </div>
              ) : (
                <div className="AutoboyExpress-dash-earnings-breakdown">
                  <div className="AutoboyExpress-dash-breakdown-item">
                    <span>Total Sales Revenue</span>
                    <span>{formatCurrency(earningsData.totalRevenue)}</span>
                  </div>
                  <div className="AutoboyExpress-dash-breakdown-item">
                    <span>Total Earned (Lifetime)</span>
                    <span style={{ color: '#22C55E' }}>{formatCurrency(walletAnalytics.totalEarned)}</span>
                  </div>
                  <div className="AutoboyExpress-dash-breakdown-item">
                    <span>Total Withdrawn</span>
                    <span style={{ color: '#8B5CF6' }}>{formatCurrency(walletAnalytics.totalWithdrawn)}</span>
                  </div>
                  {walletAnalytics.totalFees > 0 && (
                    <div className="AutoboyExpress-dash-breakdown-item">
                      <span>Platform Fees</span>
                      <span style={{ color: '#EF4444' }}>-{formatCurrency(walletAnalytics.totalFees)}</span>
                    </div>
                  )}
                  <hr />
                  <div className="AutoboyExpress-dash-breakdown-item">
                    <span>Available Balance</span>
                    <span style={{ color: '#22C55E', fontWeight: 600 }}>{formatCurrency(walletBalance.balance)}</span>
                  </div>
                  <div className="AutoboyExpress-dash-breakdown-item">
                    <span>Held in Escrow</span>
                    <span style={{ color: '#3B82F6', fontWeight: 600 }}>{formatCurrency(walletBalance.heldBalance)}</span>
                  </div>
                  <div className="AutoboyExpress-dash-breakdown-item AutoboyExpress-dash-breakdown-total">
                    <span>Total Wallet Balance</span>
                    <span>{formatCurrency(walletBalance.balance + walletBalance.heldBalance)}</span>
                  </div>
                  {earningsData.profitMargin > 0 && (
                    <div className="AutoboyExpress-dash-breakdown-item" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #E5E7EB' }}>
                      <span>Profit Margin</span>
                      <span style={{ color: '#22C55E', fontWeight: 600 }}>{earningsData.profitMargin.toFixed(1)}%</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Transaction History — real wallet transactions */}
          <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
            <div className="AutoboyExpress-dash-section-header">
              <h2 className="AutoboyExpress-dash-section-title">Recent Transactions</h2>
              <button className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary" onClick={() => setActiveSection('wallet')}>
                View All
              </button>
            </div>
            <div className="AutoboyExpress-dash-section-content">
              {isLoadingTransactions ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
                </div>
              ) : walletTransactions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  <FontAwesomeIcon icon={faWallet} size="3x" style={{ marginBottom: '1rem', opacity: 0.4 }} />
                  <p>No transactions yet</p>
                </div>
              ) : (
                <TableCard>
                  <DataTable
                    columns={[{ width: '16%' }, { width: '14%' }, { width: '38%' }, { width: '18%' }, { width: '14%' }]}
                    minWidth="680px"
                  >
                    <THead>
                      <Th>Date</Th>
                      <Th>Type</Th>
                      <Th>Description</Th>
                      <Th align="right">Amount</Th>
                      <Th>Status</Th>
                    </THead>
                    <tbody>
                      {walletTransactions.slice(0, 10).map((t, i) => (
                        <Tr key={t.id || t._id || i}>
                          <Td className="tw-text-zinc-500 dark:tw-text-zinc-400">
                            {new Date(t.created_at || t.date || t.timestamp).toLocaleDateString()}
                          </Td>
                          <Td>
                            <Badge tone={isCreditTransaction(t) ? 'green' : 'amber'}>
                              {t.type || t.transaction_type || 'N/A'}
                            </Badge>
                          </Td>
                          <Td title={t.description || t.narration || t.note || ''}>
                            {t.description || t.narration || t.note || '—'}
                          </Td>
                          <Td
                            align="right"
                            className={`tw-font-bold tw-tabular-nums ${isCreditTransaction(t) ? 'tw-text-green-600 dark:tw-text-green-400' : 'tw-text-red-500'}`}
                          >
                            {isCreditTransaction(t) ? '+' : '-'}{formatCurrency(Math.abs(t.amount || 0))}
                          </Td>
                          <Td>
                            <Badge tone={statusTone(t.status || 'pending')}>{t.status || 'pending'}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </DataTable>
                </TableCard>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAnalyticsContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      {/* Performance summary row */}
      <div className="AutoboyExpress-dash-stats">
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon" style={{ color: performanceMetrics.growthRate >= 0 ? '#22C55E' : '#EF4444' }}><FontAwesomeIcon icon={faChartLine} /></div></div>
          <div className="AutoboyExpress-dash-stat-value" style={{ color: performanceMetrics.growthRate >= 0 ? '#22C55E' : '#EF4444' }}>
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : `${performanceMetrics.growthRate >= 0 ? '+' : ''}${performanceMetrics.growthRate.toFixed(1)}%`}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Growth Rate</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faChartBar} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(performanceMetrics.averageOrderValue)}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Avg Order Value</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faShoppingCart} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : performanceMetrics.totalOrders}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Total Orders</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon" style={{ color: '#F59E0B' }}><FontAwesomeIcon icon={faStar} /></div></div>
          <div className="AutoboyExpress-dash-stat-value" style={{ color: '#F59E0B' }}>
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : performanceMetrics.sellerRating > 0 ? `${performanceMetrics.sellerRating.toFixed(1)}/5` : '—'}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Seller Rating {performanceMetrics.totalRatings > 0 ? `(${performanceMetrics.totalRatings})` : ''}</div>
        </div>
        <div className="AutoboyExpress-dash-stat-card">
          <div className="AutoboyExpress-dash-stat-header"><div className="AutoboyExpress-dash-stat-icon"><FontAwesomeIcon icon={faNairaSign} /></div></div>
          <div className="AutoboyExpress-dash-stat-value">
            {isLoadingAnalytics ? <FontAwesomeIcon icon={faSpinner} spin /> : formatCurrency(salesAnalytics.monthlyRevenue)}
          </div>
          <div className="AutoboyExpress-dash-stat-label">Monthly Revenue</div>
        </div>
      </div>

      <div className="AutoboyExpress-dash-content">
        {/* Sales Trend Chart */}
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Sales Trend</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {isLoadingAnalytics ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p style={{ marginTop: '0.75rem' }}>Loading chart...</p>
              </div>
            ) : salesAnalytics.weeklySales.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                <FontAwesomeIcon icon={faChartLine} size="2x" style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
                <p>No sales data for this period</p>
              </div>
            ) : (
              <div className="AutoboyExpress-dash-chart-container">
                <Line data={salesChartData} options={chartOptions} />
              </div>
            )}
          </div>
        </div>

        {/* Product Categories Chart */}
        <div className="AutoboyExpress-dash-section">
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Product Categories</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            {isLoadingAnalytics ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              </div>
            ) : productAnalytics.topCategories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                <FontAwesomeIcon icon={faChartPie} size="2x" style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
                <p>No category data yet</p>
              </div>
            ) : (
              <div className="AutoboyExpress-dash-chart-container">
                <Doughnut data={categoryChartData} options={doughnutOptions} />
              </div>
            )}
          </div>
        </div>

        {/* Top Products Table */}
        {productAnalytics.topProducts.length > 0 && (
          <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
            <div className="AutoboyExpress-dash-section-header">
              <h2 className="AutoboyExpress-dash-section-title">Top Performing Products</h2>
            </div>
            <div className="AutoboyExpress-dash-section-content">
              <TableCard>
                <DataTable columns={[{ width: '8%' }, { width: '52%' }, { width: '18%' }, { width: '22%' }]} minWidth="560px">
                  <THead>
                    <Th align="center">#</Th>
                    <Th>Product</Th>
                    <Th align="right">Units Sold</Th>
                    <Th align="right">Revenue</Th>
                  </THead>
                  <tbody>
                    {productAnalytics.topProducts.map((p, i) => (
                      <Tr key={i}>
                        <Td align="center" className="tw-font-bold tw-text-zinc-400">{i + 1}</Td>
                        <Td className="tw-font-medium" title={p.name || p.title || p.product_title || 'Product'}>
                          {p.name || p.title || p.product_title || 'Product'}
                        </Td>
                        <Td align="right" className="tw-tabular-nums">{p.sales || p.sold_count || p.units_sold || 0}</Td>
                        <Td align="right" className="tw-font-semibold tw-tabular-nums">
                          {formatCurrency(p.revenue || p.total_revenue || 0)}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </DataTable>
              </TableCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderWalletContent = () => {
    const totalBalance = walletBalance.balance + walletBalance.heldBalance;

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="AutoboyExpress-dash-content">
          {/* Enhanced Wallet Header — same structure as PremiumSellerDashboard's
              redesign: two independently-wrapping flex items instead of one
              row with a fixed-size icon circle and an unconstrained total
              figure fighting over the same space, which is what was
              clipping "Total Balance"/the amount at real desktop widths. */}
          <div className="AutoboyExpress-wallet-header-new">
            <div className="AutoboyExpress-wallet-header-left">
              <div className="AutoboyExpress-wallet-title-section">
                <h1 className="AutoboyExpress-wallet-main-title">
                  <FontAwesomeIcon icon={faWallet} className="AutoboyExpress-wallet-title-icon" />
                  My Wallet
                </h1>
                <p className="AutoboyExpress-wallet-description">Manage your earnings and withdrawals</p>
              </div>
            </div>
            <div className="AutoboyExpress-wallet-header-right">
              <div className="AutoboyExpress-wallet-total-card">
                <div className="AutoboyExpress-wallet-total-content">
                  <span className="AutoboyExpress-wallet-total-label">Total Balance</span>
                  <span className="AutoboyExpress-wallet-total-amount">{formatCurrency(totalBalance)}</span>
                  <div className="AutoboyExpress-wallet-total-badges">
                    <span className="AutoboyExpress-wallet-badge available">
                      <FontAwesomeIcon icon={faCheckCircle} /> Available: {formatCurrency(walletBalance.balance)}
                    </span>
                    <span className="AutoboyExpress-wallet-badge held">
                      <FontAwesomeIcon icon={faClipboardList} /> Held: {formatCurrency(walletBalance.heldBalance)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Balance Cards */}
          <div className="AutoboyExpress-wallet-cards-grid">
            {/* Available Balance Card */}
            <div className="AutoboyExpress-wallet-balance-card AutoboyExpress-wallet-available">
              <div className="AutoboyExpress-wallet-card-icon">
                <FontAwesomeIcon icon={faWallet} />
              </div>
              <div className="AutoboyExpress-wallet-card-content">
                <span className="AutoboyExpress-wallet-card-label">Available Balance</span>
                <h2 className="AutoboyExpress-wallet-card-amount">{formatCurrency(walletBalance.balance)}</h2>
                <span className="AutoboyExpress-wallet-card-subtitle">Ready to withdraw</span>
              </div>
              <div className="AutoboyExpress-wallet-card-decoration"></div>
            </div>

            {/* Held Balance Card */}
            <div className="AutoboyExpress-wallet-balance-card AutoboyExpress-wallet-held">
              <div className="AutoboyExpress-wallet-card-icon">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <div className="AutoboyExpress-wallet-card-content">
                <span className="AutoboyExpress-wallet-card-label">Held Balance</span>
                <h2 className="AutoboyExpress-wallet-card-amount">{formatCurrency(walletBalance.heldBalance)}</h2>
                <span className="AutoboyExpress-wallet-card-subtitle">Pending withdrawals</span>
              </div>
              <div className="AutoboyExpress-wallet-card-decoration"></div>
            </div>

            {/* Quick Actions Card */}
            <div className="AutoboyExpress-wallet-actions-card">
              <h3 className="AutoboyExpress-wallet-actions-title">Quick Actions</h3>
              <div className="AutoboyExpress-wallet-actions-buttons">
                <button
                  className="AutoboyExpress-wallet-action-btn AutoboyExpress-wallet-topup-btn"
                  onClick={() => setShowTopupModal(true)}
                  style={{ background: '#22C55E', color: '#fff' }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Top Up</span>
                </button>
                <button
                  className="AutoboyExpress-wallet-action-btn AutoboyExpress-wallet-withdraw-btn"
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={walletBalance.balance < 1000}
                >
                  <FontAwesomeIcon icon={faDollarSign} />
                  <span>Withdraw</span>
                </button>
                <button
                  className="AutoboyExpress-wallet-action-btn AutoboyExpress-wallet-history-btn"
                  onClick={() => setShowBankAccounts(true)}
                  style={{ background: '#3B82F6', color: '#fff' }}
                >
                  <FontAwesomeIcon icon={faUniversity} />
                  <span>Bank Accounts</span>
                </button>
              </div>
              {walletBalance.balance < 1000 && (
                <div className="AutoboyExpress-wallet-minimum-notice">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.5rem' }} />
                  Minimum withdrawal: ₦1,000
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Transaction History */}
          <div className="AutoboyExpress-wallet-transactions">
            <div className="AutoboyExpress-wallet-transactions-header">
              <div>
                <h2 className="AutoboyExpress-wallet-transactions-title">
                  <FontAwesomeIcon icon={faHistory} style={{ marginRight: '0.75rem' }} />
                  Transaction History
                </h2>
                <p className="AutoboyExpress-wallet-transactions-subtitle">Recent wallet activity</p>
              </div>
              <div className="AutoboyExpress-wallet-transactions-actions">
                <button className="AutoboyExpress-wallet-filter-btn">
                  <FontAwesomeIcon icon={faFilter} />
                  Filter
                </button>
              </div>
            </div>

            <div className="AutoboyExpress-wallet-transactions-content">
              {isLoadingWallet ? (
                <div className="AutoboyExpress-wallet-empty-state">
                  <div className="AutoboyExpress-wallet-loading-spinner"></div>
                  <p>Loading transactions...</p>
                </div>
              ) : walletTransactions.length === 0 ? (
                <div className="AutoboyExpress-wallet-empty-state">
                  <div className="AutoboyExpress-wallet-empty-icon">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <h3>No transactions yet</h3>
                  <p>Your transaction history will appear here</p>
                </div>
              ) : (
                <div className="AutoboyExpress-wallet-transactions-list">
                  {Array.isArray(walletTransactions) && walletTransactions.map((transaction, index) => (
                    <div key={transaction._id || transaction.id || index} className="AutoboyExpress-wallet-transaction-item">
                      <div className="AutoboyExpress-wallet-transaction-icon-wrapper">
                        <div className={`AutoboyExpress-wallet-transaction-icon ${isCreditTransaction(transaction) ? 'credit' : 'debit'}`}>
                          <FontAwesomeIcon icon={isCreditTransaction(transaction) ? faCheckCircle : faTimes} />
                        </div>
                      </div>

                      <div className="AutoboyExpress-wallet-transaction-details">
                        <div className="AutoboyExpress-wallet-transaction-main">
                          <span className="AutoboyExpress-wallet-transaction-desc">
                            {transaction.description || 'Transaction'}
                          </span>
                          <span className={`AutoboyExpress-wallet-transaction-amount ${isCreditTransaction(transaction) ? 'credit' : 'debit'}`}>
                            {isCreditTransaction(transaction) ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount || 0))}
                          </span>
                        </div>
                        <div className="AutoboyExpress-wallet-transaction-meta">
                          <span className="AutoboyExpress-wallet-transaction-date">
                            <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.375rem', fontSize: '0.75rem' }} />
                            {format(new Date(transaction.created_at), 'MMM d, yyyy - h:mm a')}
                          </span>
                          <span className={`AutoboyExpress-wallet-transaction-status ${transaction.status === 'completed' ? 'completed' : 'pending'}`}>
                            {transaction.status === 'completed' ? 'Completed' : 'Pending'}
                          </span>
                        </div>
                      </div>

                      <div className={`AutoboyExpress-wallet-transaction-type-badge ${isCreditTransaction(transaction) ? 'credit' : 'debit'}`}>
                        {transactionTypeLabel(transaction)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Withdrawal Modal */}
        {showWithdrawModal && (
          <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowWithdrawModal(false)}>
            <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()}>
              <div className="AutoboyExpress-dash-modal-header">
                <h2>Request Withdrawal</h2>
                <button className="AutoboyExpress-dash-modal-close" onClick={() => setShowWithdrawModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleWithdrawalSubmit}>
                <div className="AutoboyExpress-dash-modal-body">
                  <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                    Available Balance: <strong>{formatCurrency(walletBalance.balance)}</strong>
                  </p>

                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="amount">Withdrawal Amount (₦) *</label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={withdrawalForm.amount}
                      onChange={handleWithdrawalFormChange}
                      min="1000"
                      max={walletBalance.balance}
                      required
                      placeholder="Minimum ₦1,000"
                    />
                    <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                      Minimum withdrawal amount is ₦1,000
                    </p>
                  </div>

                  {/* Saved Bank Accounts Section */}
                  {userBankAccounts.length > 0 && (
                    <div className="AutoboyExpress-dash-form-group">
                      <label htmlFor="savedBankAccount">Select Saved Bank Account</label>
                      <select
                        id="savedBankAccount"
                        value={withdrawalForm.selectedBankId}
                        onChange={handleBankAccountSelect}
                        style={{
                          padding: '0.75rem',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          fontSize: '1rem',
                          width: '100%'
                        }}
                      >
                        <option value="">-- Select a bank account or enter manually --</option>
                        {userBankAccounts.map((account) => (
                          <option key={account.id || account._id} value={account.id || account._id}>
                            {account.bank_name} - {account.account_number} {account.is_verified ? '✓ Verified' : ''}
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                        {userBankAccounts.filter(acc => acc.is_verified).length > 0 ? (
                          <>✓ {userBankAccounts.filter(acc => acc.is_verified).length} verified account(s) available</>
                        ) : (
                          <>⚠️ No verified accounts. Please add and verify a bank account first.</>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Manual Entry Fields */}
                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="bankName">
                      Bank Name *
                      {withdrawalForm.selectedBankId && (
                        <span style={{ color: '#22C55E', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> From saved account
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="bankName"
                      name="bankName"
                      value={withdrawalForm.bankName}
                      onChange={handleWithdrawalFormChange}
                      required
                      placeholder="e.g., GTBank"
                      readOnly={!!withdrawalForm.selectedBankId}
                      style={{
                        background: withdrawalForm.selectedBankId ? '#F0FDF4' : 'white',
                        cursor: withdrawalForm.selectedBankId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="accountName">
                      Account Name *
                      {withdrawalForm.selectedBankId && (
                        <span style={{ color: '#22C55E', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> Verified
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="accountName"
                      name="accountName"
                      value={withdrawalForm.accountName}
                      onChange={handleWithdrawalFormChange}
                      required
                      placeholder="Account holder name"
                      readOnly={!!withdrawalForm.selectedBankId}
                      style={{
                        background: withdrawalForm.selectedBankId ? '#F0FDF4' : 'white',
                        cursor: withdrawalForm.selectedBankId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  <div className="AutoboyExpress-dash-form-group">
                    <label htmlFor="bankAccount">
                      Account Number *
                      {withdrawalForm.selectedBankId && (
                        <span style={{ color: '#22C55E', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCheckCircle} /> Verified
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="bankAccount"
                      name="bankAccount"
                      value={withdrawalForm.bankAccount}
                      onChange={handleWithdrawalFormChange}
                      required
                      pattern="[0-9]{10}"
                      placeholder="10-digit account number"
                      readOnly={!!withdrawalForm.selectedBankId}
                      style={{
                        background: withdrawalForm.selectedBankId ? '#F0FDF4' : 'white',
                        cursor: withdrawalForm.selectedBankId ? 'not-allowed' : 'text'
                      }}
                    />
                  </div>

                  {/* Add/Manage Bank Accounts Button */}
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: '#F3F4F6',
                    borderRadius: '8px',
                    border: '1px dashed #D1D5DB'
                  }}>
                    <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faUniversity} style={{ marginRight: '0.5rem' }} />
                      {userBankAccounts.length === 0 ? (
                        <>No saved bank accounts yet. Add a verified account for faster withdrawals.</>
                      ) : (
                        <>Manage your saved bank accounts or add a new verified account.</>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowWithdrawModal(false);
                        setShowBankAccounts(true);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      {userBankAccounts.length === 0 ? 'Add Bank Account' : 'Manage Bank Accounts'}
                    </button>
                  </div>
                </div>

                <div className="AutoboyExpress-dash-modal-footer">
                  <button
                    type="button"
                    className="AutoboyExpress-dash-secondary-button"
                    onClick={() => setShowWithdrawModal(false)}
                    disabled={isProcessingWithdrawal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="AutoboyExpress-dash-primary-button"
                    disabled={isProcessingWithdrawal}
                  >
                    {isProcessingWithdrawal ? 'Processing...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Top-Up Modal */}
        {showTopupModal && (
          <div className="AutoboyExpress-dash-modal-overlay" onClick={() => setShowTopupModal(false)}>
            <div className="AutoboyExpress-dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
              <div className="AutoboyExpress-dash-modal-header">
                <h2>Top Up Wallet</h2>
                <button
                  className="AutoboyExpress-dash-modal-close"
                  onClick={() => setShowTopupModal(false)}
                  disabled={isProcessingTopup}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="AutoboyExpress-dash-modal-body">
                <p style={{ marginBottom: '1.5rem', color: '#6B7280' }}>
                  Enter the amount you want to add to your wallet. Minimum amount is ₦100.
                </p>
                <div className="AutoboyExpress-dash-form-group">
                  <label>Amount (NGN)</label>
                  <input
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                    placeholder="Enter amount"
                    min="100"
                    step="100"
                    disabled={isProcessingTopup}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>
              <div className="AutoboyExpress-dash-modal-footer">
                <button
                  className="AutoboyExpress-dash-secondary-button"
                  onClick={() => setShowTopupModal(false)}
                  disabled={isProcessingTopup}
                >
                  Cancel
                </button>
                <button
                  className="AutoboyExpress-dash-primary-button"
                  onClick={handleWalletTopup}
                  disabled={isProcessingTopup || !topupAmount}
                >
                  {isProcessingTopup ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span style={{ marginLeft: '0.5rem' }}>Processing...</span>
                    </>
                  ) : (
                    `Top Up ₦${topupAmount || '0'}`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bank Accounts Manager */}
        {showBankAccounts && (
          <BankAccountsManager
            onClose={() => {
              setShowBankAccounts(false);
              // Refresh bank accounts list after managing accounts
              fetchUserBankAccounts();
            }}
          />
        )}
      </div>
    );
  };

  const renderBadgesContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section">
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title"><FontAwesomeIcon icon={faStar} style={{ marginRight: '0.5rem', color: '#F59E0B' }} />Badges & Achievements</h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <div style={{ textAlign: 'center', padding: '1rem 0 1.5rem', borderBottom: '1px solid #f0f0f0', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#22C55E', lineHeight: 1 }}>{badgePoints}</div>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.3rem' }}>Reward Points</p>
          </div>
          {userBadges.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem' }}>
              {userBadges.map((b, idx) => (
                <div key={b.badge?.id || idx} style={{ textAlign: 'center', padding: '1.25rem 1rem', borderRadius: '12px', border: '1px solid #e5e7eb', background: '#fafafa' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{b.badge?.icon || '🏆'}</div>
                  <p style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{b.badge?.name || 'Badge'}</p>
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{b.badge?.description || ''}</p>
                  {b.earned_at && <p style={{ fontSize: '0.7rem', color: '#22C55E', marginTop: '0.5rem' }}>Earned {new Date(b.earned_at).toLocaleDateString()}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', opacity: 0.6 }}>
              <FontAwesomeIcon icon={faStar} size="3x" style={{ color: '#F59E0B', marginBottom: '1rem' }} />
              <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>No badges yet</p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>Keep selling to earn badges and rewards!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderHelpContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-content">
        <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
          <div className="AutoboyExpress-dash-section-header">
            <h2 className="AutoboyExpress-dash-section-title">Help & Support</h2>
          </div>
          <div className="AutoboyExpress-dash-section-content">
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <FontAwesomeIcon icon={faHeadphones} size="3x" style={{ color: '#22C55E', marginBottom: '1.5rem' }} />
              <h3 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>We're Here to Help</h3>
              <p style={{ marginBottom: '2rem', color: '#6B7280' }}>
                Need assistance? Get in touch with our support team.
              </p>

              <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ padding: '1.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', textAlign: 'left' }}>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ color: '#22C55E' }} />
                    Email Support
                  </h4>
                  <p style={{ color: '#6B7280', marginBottom: '0.5rem' }}>For general inquiries and support</p>
                  <a href="mailto:support@AutoboyExpress.com" style={{ color: '#22C55E', fontWeight: '600' }}>
                    support@AutoboyExpress.com
                  </a>
                </div>

                <div style={{ padding: '1.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', textAlign: 'left' }}>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FontAwesomeIcon icon={faPhone} style={{ color: '#22C55E' }} />
                    Phone Support
                  </h4>
                  <p style={{ color: '#6B7280', marginBottom: '0.5rem' }}>Available Mon-Fri, 9AM-5PM WAT</p>
                  <a href="tel:+2348012345678" style={{ color: '#22C55E', fontWeight: '600' }}>
                    +234 801 234 5678
                  </a>
                </div>

                <div style={{ padding: '1.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', textAlign: 'left' }}>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FontAwesomeIcon icon={faQuestionCircle} style={{ color: '#22C55E' }} />
                    FAQs & Documentation
                  </h4>
                  <p style={{ color: '#6B7280', marginBottom: '0.5rem' }}>Find answers to common questions</p>
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-secondary"
                    onClick={() => navigate('/help')}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Visit Help Center
                  </button>
                </div>

                <div style={{ padding: '1.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', textAlign: 'left' }}>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FontAwesomeIcon icon={faHeadset} style={{ color: '#22C55E' }} />
                    Live Chat Support
                  </h4>
                  <p style={{ color: '#6B7280', marginBottom: '0.5rem' }}>Get instant support via live chat</p>
                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                    onClick={() => navigate('/support')}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Start Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsContent = () => {
    const tabs = [
      { id: 'profile', label: 'Profile', icon: faUser },
      { id: 'security', label: 'Security', icon: faLock },
      { id: 'notifications', label: 'Notifications', icon: faBell },
      { id: 'sessions', label: 'Sessions', icon: faShieldAlt },
      { id: 'account', label: 'Account', icon: faTrashAlt },
    ];
    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        <div className="bd-settings-layout">
          <nav className="bd-settings-nav">
            {tabs.map(item => (
              <button
                key={item.id}
                className={`bd-settings-nav-item${settingsTab === item.id ? ' active' : ''}`}
                onClick={() => setSettingsTab(item.id)}
              >
                <FontAwesomeIcon icon={item.icon} className="bd-settings-nav-icon" />
                {item.label}
              </button>
            ))}
          </nav>
          <div className="bd-settings-panel">
            {settingsTab === 'profile' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faUser} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Store Profile</h3>
                    <p className="bd-settings-card-sub">Update your personal and business information</p>
                  </div>
                </div>
                <div className="bd-settings-avatar-row">
                  <div className="bd-settings-avatar-wrap">
                    <img
                      src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=150`}
                      alt="Avatar"
                      className="bd-settings-avatar-img"
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=150`; }}
                    />
                  </div>
                  <div className="bd-settings-avatar-controls">
                    <div className="bd-settings-avatar-toggle">
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'url' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('url')}>URL</button>
                      <button type="button" className={`bd-settings-avatar-toggle-btn${avatarUploadMethod === 'file' ? ' active' : ''}`} onClick={() => setAvatarUploadMethod('file')}>Upload</button>
                    </div>
                    {avatarUploadMethod === 'url' ? (
                      <input className="bd-settings-input" type="url" placeholder="https://example.com/photo.jpg" value={userProfile?.avatar || ''} onChange={e => { setUserProfile({ ...userProfile, avatar: e.target.value }); setAvatarPreview(e.target.value); }} />
                    ) : (
                      <>
                        <input ref={avatarFileInputRef} type="file" accept="image/*" onChange={handleAvatarFileSelect} style={{ display: 'none' }} />
                        <button className="bd-settings-btn-outline" type="button" onClick={() => avatarFileInputRef.current?.click()}>
                          <FontAwesomeIcon icon={faUpload} /> {selectedAvatarFile ? selectedAvatarFile.name : 'Choose Image'}
                        </button>
                      </>
                    )}
                    <button className="bd-settings-btn-primary" type="button" onClick={handleAvatarUpload} disabled={uploadingAvatar}>
                      {uploadingAvatar ? <><FontAwesomeIcon icon={faSpinner} spin /> Uploading…</> : <><FontAwesomeIcon icon={faSave} /> Save Photo</>}
                    </button>
                  </div>
                </div>
                <form className="bd-settings-form" onSubmit={handleSettingsSave}>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>First Name</label>
                      <input className="bd-settings-input" type="text" value={settingsForm.firstName} onChange={e => setSettingsForm({ ...settingsForm, firstName: e.target.value })} placeholder="First name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Last Name</label>
                      <input className="bd-settings-input" type="text" value={settingsForm.lastName} onChange={e => setSettingsForm({ ...settingsForm, lastName: e.target.value })} placeholder="Last name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Email</label>
                      <input className="bd-settings-input" type="email" value={userProfile.email || ''} readOnly />
                    </div>
                    <div className="bd-settings-field">
                      <label>Phone</label>
                      <input className="bd-settings-input" type="tel" value={settingsForm.phone} onChange={e => setSettingsForm({ ...settingsForm, phone: e.target.value })} placeholder="+234 XXX XXX XXXX" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Business / Shop Name</label>
                      <input className="bd-settings-input" type="text" value={settingsForm.businessName} onChange={e => setSettingsForm({ ...settingsForm, businessName: e.target.value })} placeholder="Your business or shop name" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Business Phone</label>
                      <input className="bd-settings-input" type="tel" value={settingsForm.businessPhone} onChange={e => setSettingsForm({ ...settingsForm, businessPhone: e.target.value })} placeholder="Business contact number" />
                    </div>
                    <div className="bd-settings-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Business Address</label>
                      <input className="bd-settings-input" type="text" value={settingsForm.businessAddress} onChange={e => setSettingsForm({ ...settingsForm, businessAddress: e.target.value })} placeholder="Business location / address" />
                    </div>
                    <div className="bd-settings-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Bio / Store Description</label>
                      <textarea className="bd-settings-input" rows="3" value={settingsForm.bio} onChange={e => setSettingsForm({ ...settingsForm, bio: e.target.value })} placeholder="Tell customers about your business..." style={{ resize: 'vertical' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
                    <button type="submit" className="bd-settings-btn-primary" disabled={isSavingSettings}>
                      <FontAwesomeIcon icon={isSavingSettings ? faSpinner : faSave} spin={isSavingSettings} />
                      {isSavingSettings ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button type="button" className="bd-settings-btn-outline" onClick={() => {
                      const userId = JSON.parse(localStorage.getItem('user') || '{}')._id || JSON.parse(localStorage.getItem('user') || '{}').id;
                      if (userId) window.open(`/store/${userId}`, '_blank');
                      else setToast({ message: 'Unable to load store profile', type: 'error' });
                    }}>
                      <FontAwesomeIcon icon={faStore} /> View Store
                    </button>
                  </div>
                </form>
              </div>
            )}
            {settingsTab === 'security' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faLock} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Security</h3>
                    <p className="bd-settings-card-sub">Change your password and manage two-factor authentication</p>
                  </div>
                </div>
                <form className="bd-settings-form" onSubmit={handleChangePassword}>
                  <h4 style={{ marginBottom: '1rem', fontWeight: 600 }}>Change Password</h4>
                  <div className="bd-settings-field">
                    <label>Current Password</label>
                    <input className="bd-settings-input" type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} placeholder="Enter current password" autoComplete="current-password" />
                  </div>
                  <div className="bd-settings-grid-2">
                    <div className="bd-settings-field">
                      <label>New Password</label>
                      <input className="bd-settings-input" type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} placeholder="New password" autoComplete="new-password" />
                    </div>
                    <div className="bd-settings-field">
                      <label>Confirm New Password</label>
                      <input className="bd-settings-input" type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} placeholder="Confirm new password" autoComplete="new-password" />
                    </div>
                  </div>
                  <button type="submit" className="bd-settings-btn-primary" style={{ marginTop: '1rem' }} disabled={savingPassword}>
                    <FontAwesomeIcon icon={savingPassword ? faSpinner : faLock} spin={savingPassword} />
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </button>
                </form>
                <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid var(--bd-border)' }} />
                <TwoFactorAuth isDarkMode={isDarkMode} />
              </div>
            )}
            {settingsTab === 'notifications' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faBell} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Notifications</h3>
                    <p className="bd-settings-card-sub">Choose how you receive updates and alerts</p>
                  </div>
                </div>
                <div className="bd-notif-list">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications', sub: 'Receive notifications via email' },
                    { key: 'pushNotifications', label: 'Push Notifications', sub: 'Browser and app push alerts' },
                    { key: 'smsNotifications', label: 'SMS Notifications', sub: 'Text message alerts' },
                    { key: 'orderUpdates', label: 'Order Updates', sub: 'New orders and status changes' },
                    { key: 'priceAlerts', label: 'Price Alerts', sub: 'Notifications about your product pricing' },
                    { key: 'promotions', label: 'Promotions & Marketing', sub: 'Platform deals and campaigns' },
                    { key: 'newMessages', label: 'New Messages', sub: 'Buyer inquiries and chat messages' },
                  ].map(({ key, label, sub }) => (
                    <div key={key} className="bd-notif-item">
                      <div className="bd-notif-item-label">
                        {label}
                        <span className="bd-notif-item-sub">{sub}</span>
                      </div>
                      <label className="bd-toggle">
                        <input type="checkbox" checked={notificationPrefs[key] ?? false} onChange={e => setNotificationPrefs(p => ({ ...p, [key]: e.target.checked }))} />
                        <span className="bd-toggle-track"><span className="bd-toggle-thumb" /></span>
                      </label>
                    </div>
                  ))}
                </div>
                <button className="bd-settings-btn-primary" style={{ marginTop: '1.25rem' }} onClick={handleSaveNotifPrefs} disabled={savingNotifPrefs}>
                  <FontAwesomeIcon icon={savingNotifPrefs ? faSpinner : faSave} spin={savingNotifPrefs} />
                  {savingNotifPrefs ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            )}
            {settingsTab === 'sessions' && (
              <div className="bd-settings-card">
                <div className="bd-settings-card-header">
                  <FontAwesomeIcon icon={faShieldAlt} className="bd-settings-card-icon" />
                  <div>
                    <h3 className="bd-settings-card-title">Active Sessions</h3>
                    <p className="bd-settings-card-sub">Manage devices logged into your account</p>
                  </div>
                  {activeSessions.length > 1 && (
                    <button className="bd-settings-btn-danger-outline" style={{ marginLeft: 'auto' }} onClick={handleTerminateAllSessions}>
                      Sign Out Other Devices
                    </button>
                  )}
                </div>
                {sessionsLoading ? (
                  <p style={{ color: '#6b7280', padding: '1rem' }}><FontAwesomeIcon icon={faSpinner} spin /> Loading sessions...</p>
                ) : activeSessions.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>No active sessions found.</p>
                ) : (
                  <div className="bd-session-list">
                    {activeSessions.map((session, idx) => {
                      const sessionId = session.id || session._id || idx;
                      const isCurrent = session.is_current || session.isCurrent;
                      return (
                        <div key={sessionId} className="bd-session-item">
                          <div>
                            <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem' }}>
                              {session.device_name || session.device || session.browser || 'Unknown Device'}
                              {isCurrent && <span className="bd-session-badge">This device</span>}
                            </p>
                            <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {session.ip_address || ''}{session.location ? ` · ${session.location}` : ''}
                              {session.last_active ? ` · ${new Date(session.last_active).toLocaleString()}` : ''}
                            </p>
                          </div>
                          {!isCurrent && (
                            <button className="bd-settings-btn-danger-outline" onClick={() => handleTerminateSession(sessionId)} disabled={terminatingSession === sessionId}>
                              {terminatingSession === sessionId ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Sign Out'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {settingsTab === 'account' && (
              <>
                <div className="bd-settings-card">
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faDownload} className="bd-settings-card-icon" />
                    <div>
                      <h3 className="bd-settings-card-title">Data Management</h3>
                      <p className="bd-settings-card-sub">Export a copy of your personal data</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.9rem', color: 'var(--bd-muted)', marginBottom: '1rem' }}>
                    Request a copy of all your personal data stored on AutoboyExpress. It will be sent to your registered email.
                  </p>
                  <button className="bd-settings-btn-outline" onClick={handleExportData} disabled={exportingData}>
                    {exportingData ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</> : <><FontAwesomeIcon icon={faDownload} /> Export My Data</>}
                  </button>
                </div>
                <div className="bd-settings-card bd-settings-card-danger" style={{ marginTop: '1rem' }}>
                  <div className="bd-settings-card-header">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="bd-settings-card-icon" style={{ color: '#ef4444' }} />
                    <div>
                      <h3 className="bd-settings-card-title" style={{ color: '#ef4444' }}>Danger Zone</h3>
                      <p className="bd-settings-card-sub">Permanently delete your account and all data</p>
                    </div>
                  </div>
                  {!showDeleteConfirm ? (
                    <button className="bd-settings-btn-danger" onClick={() => setShowDeleteConfirm(true)}>
                      <FontAwesomeIcon icon={faTrashAlt} /> Delete My Account
                    </button>
                  ) : (
                    <div style={{ background: 'rgba(239,68,68,0.05)', padding: '1.25rem', borderRadius: '10px', border: '1px solid #fecaca' }}>
                      <h4 style={{ color: '#ef4444', marginBottom: '0.75rem' }}>Are you absolutely sure?</h4>
                      <p style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>Enter your password to confirm account deletion.</p>
                      <form onSubmit={handleDeleteAccount}>
                        <input type="password" className="bd-settings-input" placeholder="Confirm password" value={passwordForDeletion} onChange={e => setPasswordForDeletion(e.target.value)} style={{ marginBottom: '1rem' }} required />
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <button type="button" className="bd-settings-btn-outline" onClick={() => { setShowDeleteConfirm(false); setPasswordForDeletion(''); }}>Cancel</button>
                          <button type="submit" className="bd-settings-btn-danger" disabled={deletingAccount || !passwordForDeletion}>
                            {deletingAccount ? <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</> : 'Yes, Delete Permanently'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };
  const renderSwapDealsContent = () => {
    const STATUS_LABEL = {
      pending_seller_review:    { text: 'Awaiting Your Response', color: '#22C55E' },
      declined_by_seller:       { text: 'You Declined',           color: '#ef4444' },
      pending_initiator_review: { text: 'Buyer Reviewing Price',  color: '#6b7280' },
      declined_by_initiator:    { text: 'Buyer Declined',         color: '#ef4444' },
      accepted_pending_payment: { text: 'Awaiting Payment',       color: '#22C55E' },
      paid_in_escrow:           { text: 'In Escrow',               color: '#16A34A' },
      shipped:                  { text: 'Shipped',                color: '#0ea5e9' },
      delivered:                { text: 'Delivered',              color: '#22c55e' },
      completed:                { text: 'Completed',              color: '#22c55e' },
      cancelled:                { text: 'Cancelled',              color: '#6b7280' },
    };

    const incomingOffers = getReceivedSwaps();
    const pendingCount = incomingOffers.filter(s => s.status === 'pending_seller_review').length;

    const handleRespond = async (swapId) => {
      const amount = parseFloat(swapTopupAmounts[swapId] || 0);
      if (isNaN(amount) || amount < 0) { setToast({ message: 'Enter a valid top-up amount (0 for a free swap)', type: 'warning' }); return; }
      setSwapActionLoading(swapId + '_respond');
      try {
        await respondToOffer(swapId, amount);
        // Accepting the swap is the point the seller commits their own
        // device to it — prompt for that device's IMEI right away.
        setSwapIMEITarget(swapId);
      } finally { setSwapActionLoading(null); }
    };

    const handleDecline = async (swapId) => {
      if (!window.confirm('Decline this swap offer?')) return;
      setSwapActionLoading(swapId + '_decline');
      try { await declineOffer(swapId); } finally { setSwapActionLoading(null); }
    };

    const handleConfirmShipment = async (swapId) => {
      if (!window.confirm('Confirm that you have shipped your device to the buyer?')) return;
      setSwapActionLoading(swapId + '_ship');
      try { await confirmShipment(swapId, { tracking_number: 'N/A' }); } finally { setSwapActionLoading(null); }
    };

    const handleConfirmDelivery = async (swapId) => {
      if (!window.confirm("Confirm that you have received the buyer's device?")) return;
      setSwapActionLoading(swapId + '_deliver');
      try { await confirmDelivery(swapId, false); } finally { setSwapActionLoading(null); }
    };

    return (
      <div className="AutoboyExpress-dash-content-wrapper">
        {/* Sub-tab bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { id: 'p2p', label: 'P2P Swap Deals' },
            { id: 'requests', label: `Swap Requests${pendingCount ? ` (${pendingCount} new)` : incomingOffers.length ? ` (${incomingOffers.length})` : ''}` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSwapRequestsTab(tab.id)}
              style={{
                padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                background: swapRequestsTab === tab.id ? '#22C55E' : (isDarkMode ? '#1e293b' : '#f1f5f9'),
                color: swapRequestsTab === tab.id ? '#fff' : (isDarkMode ? '#94a3b8' : '#475569'),
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              {tab.id === 'requests' && pendingCount > 0 && (
                <span style={{
                  position: 'absolute', top: '-6px', right: '-6px',
                  background: '#ef4444', color: '#fff', borderRadius: '50%',
                  width: '18px', height: '18px', fontSize: '10px', fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{pendingCount}</span>
              )}
              {tab.label}
            </button>
          ))}
        </div>

        {/* P2P Deals (existing) */}
        {swapRequestsTab === 'p2p' && (
          <SwapDeals embedded isDarkMode={isDarkMode} />
        )}

        {/* Incoming Swap Offer Requests */}
        {swapRequestsTab === 'requests' && (
          <div>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
              Incoming Swap Requests
            </h3>

            {swapLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px' }} />
              </div>
            )}

            {!swapLoading && incomingOffers.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '48px 24px', borderRadius: '12px',
                background: isDarkMode ? '#1e293b' : '#f8fafc',
                color: isDarkMode ? '#94a3b8' : '#64748b',
              }}>
                <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '36px', marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>No swap requests yet</p>
                <p style={{ margin: 0, fontSize: '13px' }}>Buyers will send device swap offers for your listed products.</p>
              </div>
            )}

            {!swapLoading && incomingOffers.map(swap => {
              const statusInfo = STATUS_LABEL[swap.status] || { text: swap.status, color: '#6b7280' };
              const swapId = swap.id || swap._id;
              const isPending = swap.status === 'pending_seller_review';
              const listingProduct = swap.listing_product || swap.product || null;
              return (
                <div key={swapId} style={{
                  borderRadius: '12px', padding: '16px 20px', marginBottom: '12px',
                  background: isDarkMode ? '#1e293b' : '#f8fafc',
                  border: `1px solid ${isPending ? '#22C55E' : (isDarkMode ? '#334155' : '#e2e8f0')}`,
                  boxShadow: isPending ? '0 0 0 2px rgba(34,197,94,0.15)' : (isDarkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)'),
                  position: 'relative',
                }}>
                  {/* Header: offer number + status badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        #{swap.swap_number || String(swapId).slice(-8).toUpperCase()}
                      </span>
                      <h4 style={{ margin: '4px 0 2px', fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>
                        {swap.model}{swap.storage ? ` · ${swap.storage}` : ''}
                      </h4>
                      <span style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                        {swap.device_type} · Condition {swap.condition_rating}/10
                        {swap.battery_health != null ? ` · Battery ${swap.battery_health}%` : ''}
                        {swap.device_type === 'Laptop' && swap.ram ? ` · RAM ${swap.ram}` : ''}
                        {swap.repair_history ? ` · Repairs: ${swap.repair_history}` : ''}
                      </span>
                      <span style={{ display: 'block', fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8', marginTop: '2px' }}>
                        {new Date(swap.created_at || swap.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, background: statusInfo.color + '22', color: statusInfo.color, flexShrink: 0 }}>
                      {statusInfo.text}
                    </span>
                  </div>

                  {/* What the buyer wants (your listing) */}
                  {listingProduct && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', padding: '8px 10px', borderRadius: '8px', background: isDarkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}` }}>
                      {(listingProduct.images?.[0]?.url || listingProduct.images?.[0] || listingProduct.image) && (
                        <img src={listingProduct.images?.[0]?.url || listingProduct.images?.[0] || listingProduct.image} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                      )}
                      <div>
                        <div style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#94a3b8' }}>Buyer wants your listing</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#1e293b' }}>{listingProduct.title || listingProduct.name}</div>
                        {listingProduct.price && <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 700 }}>₦{Number(listingProduct.price).toLocaleString()}</div>}
                      </div>
                    </div>
                  )}

                  {/* Device images thumbnail strip */}
                  {swap.images?.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {swap.images.slice(0, 5).map((img, i) => (
                        <img key={i} src={img} alt={`Device ${i + 1}`}
                          style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ))}
                      {swap.images.length > 5 && (
                        <div style={{ width: '52px', height: '52px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isDarkMode ? '#0f172a' : '#f1f5f9', fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#94a3b8' : '#64748b' }}>+{swap.images.length - 5}</div>
                      )}
                    </div>
                  )}

                  {/* Action block: set price or decline (pending_seller_review only) */}
                  {isPending && (
                    <div style={{ marginTop: '14px', padding: '12px 14px', borderRadius: '8px', background: isDarkMode ? '#052e16' : '#f0fdf4', border: `1px solid ${isDarkMode ? '#166534' : '#bbf7d0'}` }}>
                      <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#4ade80' : '#166534' }}>
                        Set top-up price (₦) — enter 0 for an equal swap
                      </p>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                          type="number" min="0" placeholder="e.g. 50000"
                          value={swapTopupAmounts[swapId] || ''}
                          onChange={e => setSwapTopupAmounts(prev => ({ ...prev, [swapId]: e.target.value }))}
                          style={{ flex: 1, minWidth: '120px', padding: '8px 12px', borderRadius: '7px', fontSize: '14px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#1e293b' : '#fff', color: isDarkMode ? '#f1f5f9' : '#1e293b' }}
                        />
                        <button
                          disabled={swapActionLoading === swapId + '_respond'}
                          onClick={() => handleRespond(swapId)}
                          style={{ padding: '8px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#22C55E', color: '#fff', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
                        >
                          {swapActionLoading === swapId + '_respond' ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />} Send Price
                        </button>
                        <button
                          disabled={swapActionLoading === swapId + '_decline'}
                          onClick={() => handleDecline(swapId)}
                          style={{ padding: '8px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#ef4444', color: '#fff', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
                        >
                          {swapActionLoading === swapId + '_decline' ? <FontAwesomeIcon icon={faSpinner} spin /> : null} Decline
                        </button>
                      </div>

                      {/* Live fee breakdown */}
                      {swapTopupAmounts[swapId] && parseFloat(swapTopupAmounts[swapId]) > 0 && (() => {
                        try {
                          const feeData = calculateSwapFees(parseFloat(swapTopupAmounts[swapId]));
                          return (
                            <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '6px', background: isDarkMode ? '#052e16' : '#f0fdf4', border: `1px solid ${isDarkMode ? '#166534' : '#bbf7d0'}`, fontSize: '12px', color: isDarkMode ? '#f1f5f9' : '#166534' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div><span style={{ opacity: 0.8 }}>Top-up Amount:</span><div style={{ fontWeight: 600, fontSize: '13px' }}>₦{parseFloat(swapTopupAmounts[swapId]).toLocaleString()}</div></div>
                                <div><span style={{ opacity: 0.8 }}>Platform Fee ({feeData.platformFeePercentage}%):</span><div style={{ fontWeight: 600, fontSize: '13px' }}>₦{feeData.platformFee.toLocaleString()}</div></div>
                                <div><span style={{ opacity: 0.8 }}>VAT ({feeData.vatPercentage}%):</span><div style={{ fontWeight: 600, fontSize: '13px' }}>₦{feeData.vat.toLocaleString()}</div></div>
                                <div><span style={{ opacity: 0.8 }}>Buyer Pays Total:</span><div style={{ fontWeight: 700, fontSize: '13px', color: isDarkMode ? '#4ade80' : '#16A34A' }}>₦{feeData.total.toLocaleString()}</div></div>
                              </div>
                            </div>
                          );
                        } catch { return null; }
                      })()}
                    </div>
                  )}

                  {/* IMEI for the seller's OWN device — required once they've
                      accepted the swap. Stays available until submitted in
                      case the post-accept prompt was dismissed. */}
                  {!isPending && !swap.seller_imei_submitted && !swapIMEISubmittedIds.includes(swapId)
                    && !['declined_by_seller', 'declined_by_initiator', 'cancelled'].includes(swap.status) && (
                    <div style={{ marginTop: '12px' }}>
                      <button onClick={() => setSwapIMEITarget(swapId)}
                        style={{ width: '100%', padding: '9px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        <FontAwesomeIcon icon={faMobileAlt} /> Enter IMEI for Your Device
                      </button>
                    </div>
                  )}

                  {/* Post-payment fulfillment actions (seller side) */}
                  {swap.status === 'paid_in_escrow' && (
                    <div style={{ marginTop: '12px' }}>
                      <button disabled={swapActionLoading === swapId + '_ship'} onClick={() => handleConfirmShipment(swapId)}
                        style={{ width: '100%', padding: '9px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#0ea5e9', color: '#fff', fontWeight: 600, fontSize: '13px' }}>
                        Mark my Device as Shipped
                      </button>
                    </div>
                  )}

                  {swap.status === 'shipped' && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        disabled={swapActionLoading === swapId + '_deliver'}
                        onClick={() => handleConfirmDelivery(swapId)}
                        style={{ width: '100%', padding: '9px 16px', borderRadius: '7px', border: 'none', cursor: 'pointer', background: '#22c55e', color: '#fff', fontWeight: 600, fontSize: '13px' }}
                      >
                        {swapActionLoading === swapId + '_deliver' ? <FontAwesomeIcon icon={faSpinner} spin /> : '✅'} Confirm Buyer's Device Received
                      </button>
                    </div>
                  )}

                  {/* Status info for non-pending states */}
                  {!isPending && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                      {swap.status === 'pending_initiator_review' && swap.seller_topup_amount != null && (
                        <span>📨 Price sent to buyer — ₦{Number(swap.total_payable || swap.seller_topup_amount).toLocaleString()} total. Awaiting buyer decision.</span>
                      )}
                      {swap.status === 'pending_initiator_review' && swap.seller_topup_amount == null && (
                        <span>📨 Price offer sent. Awaiting buyer decision.</span>
                      )}
                      {swap.status === 'declined_by_initiator' && '❌ Buyer declined your price. The offer is closed.'}
                      {swap.status === 'declined_by_seller' && '✗ You declined this offer.'}
                      {swap.status === 'accepted_pending_payment' && '💳 Buyer accepted your price — awaiting their payment.'}
                      {swap.status === 'paid_in_escrow' && '🔒 Payment in escrow — ship your device to the buyer to proceed.'}
                      {swap.status === 'shipped' && "🚚 Your device is shipped — confirm once you've received the buyer's device."}
                      {swap.status === 'delivered' && '📬 Both devices delivered — awaiting buyer to complete the swap.'}
                      {swap.status === 'completed' && '✅ Swap completed. Funds released to your wallet.'}
                      {swap.status === 'cancelled' && '🚫 This swap was cancelled.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {swapIMEITarget && (
          <IMEISubmissionModal
            unitCount={1}
            title="IMEI — Your Swap Device"
            isDarkMode={isDarkMode}
            onSubmit={async (imeis) => {
              await swapAPI.submitSellerIMEI(swapIMEITarget, imeis[0].imei1, imeis[0].imei2);
              setSwapIMEISubmittedIds(prev => [...prev, swapIMEITarget]);
              setSwapIMEITarget(null);
              setToast({ message: 'IMEI submitted successfully', type: 'success' });
            }}
            onClose={() => setSwapIMEITarget(null)}
          />
        )}
      </div>
    );
  };

  const renderEscrowContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faLock} /> Escrow Analytics & Settings
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <EscrowAnalytics fetchOrders={() => sellerAPI.getOrders()} />
          <EscrowSettingsPanel />
          <div style={{ textAlign: 'center', padding: '3rem 1rem', marginTop: '2rem' }}>
            <FontAwesomeIcon icon={faLock} size="3x" style={{ color: '#22C55E', marginBottom: '1.5rem' }} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
              How Escrow Works for Sellers
            </h3>
            <p style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
              All your sales are protected with escrow. Funds are held securely until the buyer confirms delivery, ensuring safe transactions for both parties.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faShoppingCart} style={{ color: '#22C55E' }} />
                  Customer Orders
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  Payment is held in escrow when a customer places an order for your product
                </p>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faBox} style={{ color: '#22C55E' }} />
                  Ship Your Product
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  Process and ship the order. The payment stays in escrow until the buyer confirms delivery
                </p>
              </div>
              <div style={{ padding: '1.5rem', background: isDarkMode ? '#1F2937' : '#F9FAFB', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#374151' : '#E5E7EB'}` }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: isDarkMode ? '#F9FAFB' : '#1F2937' }}>
                  <FontAwesomeIcon icon={faDollarSign} style={{ color: '#22C55E' }} />
                  Get Paid
                </h4>
                <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                  Once the buyer confirms delivery, funds are released from escrow to your wallet automatically
                </p>
              </div>
            </div>
            <div style={{ marginTop: '2rem', padding: '1rem', background: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#4ADE80' : '#16A34A', margin: 0 }}>
                <FontAwesomeIcon icon={faShieldAlt} /> Auto-release activates after 3 days if the buyer has not responded — you get paid either way
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDisputesContent = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Disputes
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <Disputes />
        </div>
      </div>
    </div>
  );

  const renderBuyerRequests = () => (
    <div className="AutoboyExpress-dash-content-wrapper">
      <div className="AutoboyExpress-dash-section" style={{ gridColumn: '1 / -1' }}>
        <div className="AutoboyExpress-dash-section-header">
          <h2 className="AutoboyExpress-dash-section-title">
            <FontAwesomeIcon icon={faClipboardList} /> Public "Want to Buy" Requests
          </h2>
        </div>
        <div className="AutoboyExpress-dash-section-content">
          <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '2rem' }}>
            Browse what buyers are looking for. If you have a matching product, send them a link to your listing.
          </p>

          {loadingPublicRequests ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
              <p style={{ marginTop: '1rem' }}>Loading buyer requests...</p>
            </div>
          ) : publicWtbRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: '15px', border: '2px dashed #e2e8f0' }}>
              <FontAwesomeIcon icon={faSearch} size="3x" style={{ color: '#cbd5e1', marginBottom: '1.5rem' }} />
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No active requests</h3>
              <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto' }}>
                There are currently no active "Want to Buy" requests from buyers. Check back later!
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
              {publicWtbRequests.map(req => (
                <div
                  key={req.id || req._id}
                  style={{
                    background: isDarkMode ? '#1e293b' : '#fff',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid ' + (isDarkMode ? '#334155' : '#e2e8f0'),
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#3b82f6'
                    }}>
                      Open Request
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                      {format(new Date(req.created_at || Date.now()), 'MMM d')}
                    </span>
                  </div>

                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem', color: isDarkMode ? '#f8fafc' : '#1e293b' }}>
                    {req.title}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: '1rem', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {req.description}
                  </p>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                      <FontAwesomeIcon icon={faTag} style={{ color: '#22c55e' }} />
                      {req.category || 'Any Category'}
                    </div>
                    {(req.budget_min || req.budget_max) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                        <FontAwesomeIcon icon={faNairaSign} style={{ color: '#22c55e' }} />
                        {req.budget_min ? `₦${req.budget_min.toLocaleString()}` : '0'} - {req.budget_max ? `₦${req.budget_max.toLocaleString()}` : 'Any'}
                      </div>
                    )}
                    {req.location_preference && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#475569' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#22c55e' }} />
                        {req.location_preference}
                      </div>
                    )}
                  </div>

                  <button
                    className="AutoboyExpress-dash-btn AutoboyExpress-dash-btn-primary"
                    onClick={() => {
                      setRespondingToRequest(req);
                      setShowRespondModal(true);
                    }}
                    style={{ width: '100%', borderRadius: '8px' }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: '0.5rem' }} /> Respond to Request
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showRespondModal && renderRespondModal()}
    </div>
  );

  const renderRespondModal = () => (
    <div className="AutoboyExpress-premium-overlay" style={{ zIndex: 1000 }}>
      <div className="AutoboyExpress-premium-modal" style={{ maxWidth: '500px', width: '95%' }}>
        <div className="AutoboyExpress-premium-header">
          <div className="AutoboyExpress-premium-title">
            <FontAwesomeIcon icon={faPaperPlane} className="AutoboyExpress-premium-icon" />
            <h2>Respond to Buyer</h2>
          </div>
          <button className="AutoboyExpress-premium-close" onClick={() => setShowRespondModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleRespondToRequest} className="AutoboyExpress-premium-content">
          <p style={{ marginBottom: '1.5rem', color: '#6B7280', fontSize: '0.9rem' }}>
            Linking a product allows the buyer to view your listing and purchase directly.
          </p>

          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>Buyer is looking for:</h4>
            <p style={{ fontSize: '0.9rem', color: '#475569', marginTop: '0.25rem' }}>{respondingToRequest?.title}</p>
          </div>

          <div className="AutoboyExpress-dash-form-group">
            <label>Select Your Product *</label>
            <select
              value={responseForm.selectedProductId}
              onChange={(e) => setResponseForm({ ...responseForm, selectedProductId: e.target.value })}
              required
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #E5E7EB' }}
            >
              <option value="">-- Choose matching product --</option>
              {products.map(p => (
                <option key={p.id || p._id} value={p.id || p._id}>
                  {p.title} - ₦{p.price?.toLocaleString()}
                </option>
              ))}
            </select>
          </div>

          <div className="AutoboyExpress-dash-form-group" style={{ marginTop: '1rem' }}>
            <label>Message (Optional)</label>
            <textarea
              placeholder="Tell the buyer why your product is a good fit."
              value={responseForm.message}
              onChange={(e) => setResponseForm({ ...responseForm, message: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #E5E7EB', minHeight: '80px' }}
            />
          </div>

          <div className="AutoboyExpress-premium-actions" style={{ marginTop: '2rem' }}>
            <button type="button" className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-secondary" onClick={() => setShowRespondModal(false)}>
              Cancel
            </button>
            <button type="submit" className="AutoboyExpress-premium-btn AutoboyExpress-premium-btn-primary" disabled={submittingResponse}>
              {submittingResponse ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Sending...</>
              ) : (
                <><FontAwesomeIcon icon={faPaperPlane} /> Send Response</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const getContentForSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboardContent();
      case 'kyc':
        return (
          <div className="AutoboyExpress-dash-content-wrapper">
            <div className="AutoboyExpress-dash-content">
              <KYCVerification
                userType="seller"
                email={userProfile?.email || ''}
                isDarkMode={isDarkMode}
                onComplete={() => {
                  setToast({
                    message: 'KYC verification submitted successfully! We will review your documents.',
                    type: 'success'
                  });
                  setTimeout(() => setActiveSection('dashboard'), 2000);
                }}
              />
            </div>
          </div>
        );
      case 'products':
        return renderProductsContent();
      case 'orders':
        return renderOrdersContent();
      case 'earnings':
        return renderEarningsContent();
      case 'wallet':
        return renderWalletContent();
      case 'addresses':
        return (
          <AddressManagement
            onToast={setToast}
            isDarkMode={isDarkMode}
          />
        );
      case 'swap-deals':
        return renderSwapDealsContent();
      case 'escrow':
        return renderEscrowContent();
      case 'analytics':
        return renderAnalyticsContent();
      case 'badges':
        return renderBadgesContent();
      case 'buyer-requests':
        return renderBuyerRequests();
      case 'disputes':
        return renderDisputesContent();
      case 'help':
        return renderHelpContent();
      case 'settings':
        return renderSettingsContent();
      default:
        return renderDashboardContent();
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {showAddProductForm && renderAddProductForm()}
      {showViewProductModal && renderViewProductModal()}
      {showEditProductForm && renderEditProductForm()}
      <div className="AutoboyExpress-dash-container">
        {/* Sidebar */}
        <div className={`AutoboyExpress-dash-sidebar ${isMobileSidebarOpen ? 'AutoboyExpress-dash-sidebar-open' : ''}`}>
          <div className="AutoboyExpress-dash-logo">
            <img
              src={isDarkMode ? darkLogo : lightLogo}
              alt="AutoboyExpress"
            />
          </div>

          <div className="AutoboyExpress-dash-profile">
            <div className="AutoboyExpress-dash-profile-avatar" style={{ overflow: 'hidden', borderRadius: '50%', background: '#1f2937' }}>
              {(avatarPreview || userProfile?.avatar)
                ? <img src={avatarPreview || userProfile.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width:60%;height:60%;fill:#9ca3af;margin:20%"><path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304H178.3z"/></svg>'; }} />
                : <FontAwesomeIcon icon={faUser} />}
            </div>
            <div className="AutoboyExpress-dash-profile-info">
              <h3 className="AutoboyExpress-dash-seller-name">{userProfile.name}</h3>
              <p className="AutoboyExpress-dash-seller-status">
                {userProfile.isVerified ? (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '0.25rem', color: '#22C55E' }} />
                    Verified Seller
                  </>
                ) : (
                  'Seller'
                )}
              </p>
            </div>
          </div>

          <ul className="AutoboyExpress-dash-nav">
            <li className="AutoboyExpress-dash-nav-item">
              <button
                className="AutoboyExpress-dash-nav-link"
                onClick={() => {
                  navigate('/homepage');
                  setIsMobileSidebarOpen(false);
                }}
              >
                <FontAwesomeIcon icon={faHome} className="AutoboyExpress-dash-nav-icon" />
                Homepage
              </button>
            </li>
            {[
              { id: 'dashboard', icon: faChartBar, label: 'Dashboard' },
              { id: 'kyc', icon: faShieldAlt, label: 'KYC Verification' },
              { id: 'products', icon: faBox, label: 'Products' },
              { id: 'orders', icon: faShoppingCart, label: 'Orders' },
              { id: 'earnings', icon: faDollarSign, label: 'Earnings' },
              { id: 'wallet', icon: faWallet, label: 'Wallet' },
              { id: 'addresses', icon: faMapMarkerAlt, label: 'My Addresses' },
              { id: 'swap-deals', icon: faExchangeAlt, label: 'Swap Deals' },
              { id: 'escrow', icon: faLock, label: 'Escrow' },
              { id: 'analytics', icon: faChartLine, label: 'Analytics' },
              { id: 'badges', icon: faStar, label: 'Badges' },
              { id: 'buyer-requests', icon: faClipboardList, label: 'Buyer Requests' },
              { id: 'disputes', icon: faExclamationTriangle, label: 'Disputes' },
              { id: 'help', icon: faQuestionCircle, label: 'Help & Support' },
              { id: 'settings', icon: faCog, label: 'Settings' }
            ].map(item => (
              <li key={item.id} className="AutoboyExpress-dash-nav-item">
                <button
                  className={`AutoboyExpress-dash-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveSection(item.id);
                    setIsMobileSidebarOpen(false);
                  }}
                >
                  <FontAwesomeIcon icon={item.icon} className="AutoboyExpress-dash-nav-icon" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Upgrade to Premium Card */}
          <div style={{
            margin: '1.5rem 1rem',
            padding: '1.5rem',
            background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
            borderRadius: '12px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
          }}>
            <FontAwesomeIcon
              icon={faCrown}
              style={{ fontSize: '2.5rem', marginBottom: '0.75rem', color: '#FCD34D' }}
            />
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              color: 'white'
            }}>
              Go Premium!
            </h3>
            <p style={{
              fontSize: '0.85rem',
              marginBottom: '1rem',
              opacity: '0.95',
              lineHeight: '1.4'
            }}>
              Unlock advanced analytics, priority support, and exclusive seller tools.
            </p>
            <button
              onClick={() => setShowPremiumUpgrade(true)}
              style={{
                width: '100%',
                padding: '0.65rem 1rem',
                background: 'white',
                color: '#16A34A',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <FontAwesomeIcon icon={faRocket} />
              Upgrade Now
            </button>
          </div>
        </div>

        {/* Mobile Overlay */}
        {isMobileSidebarOpen && (
          <div
            className="AutoboyExpress-dash-mobile-overlay"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="AutoboyExpress-dash-main">
          {/* Header */}
          <div className="AutoboyExpress-dash-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button
                className="AutoboyExpress-dash-mobile-toggle"
                onClick={toggleMobileSidebar}
              >
                <FontAwesomeIcon icon={faBars} />
              </button>
              <h1 className="AutoboyExpress-dash-title">
                {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
              </h1>
            </div>
            <div className="AutoboyExpress-dash-header-actions">
              {/* Notifications */}
              <div className="AutoboyExpress-dash-notification-wrapper">
                <button
                  className="AutoboyExpress-dash-notification-btn"
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowSettings(false);
                  }}
                >
                  <FontAwesomeIcon icon={faBell} />
                  {notifications.filter(n => n.unread).length > 0 && (
                    <span className="AutoboyExpress-dash-notification-badge">
                      {notifications.filter(n => n.unread).length}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-notifications-dropdown">
                    <div className="AutoboyExpress-dash-dropdown-header">
                      <h3>Notifications</h3>
                      <button className="AutoboyExpress-dash-mark-read-btn">Mark all as read</button>
                    </div>
                    <div className="AutoboyExpress-dash-dropdown-content">
                      {notifications.map(notif => (
                        <div key={notif.id} className={`AutoboyExpress-dash-notification-item ${notif.unread ? 'unread' : ''}`}>
                          <div className="AutoboyExpress-dash-notification-icon">
                            <FontAwesomeIcon icon={
                              notif.type === 'order' ? faShoppingCart :
                                notif.type === 'payment' ? faDollarSign :
                                  notif.type === 'alert' ? faBell :
                                    faCheckCircle
                            } />
                          </div>
                          <div className="AutoboyExpress-dash-notification-content">
                            <h4>{notif.title}</h4>
                            <p>{notif.message}</p>
                            <span>{notif.time}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="AutoboyExpress-dash-dropdown-footer">
                      <button onClick={() => { navigate('/notifications'); setShowNotifications(false); }}>View All Notifications</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Theme Toggle */}
              <button className="AutoboyExpress-dash-theme-toggle" onClick={toggleTheme}>
                <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} />
              </button>

              {/* Profile Picture */}
              <div
                className="AutoboyExpress-dash-profile-picture"
                onClick={() => setActiveSection('settings')}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '2px solid #22C55E',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                  marginLeft: '15px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <img
                  src={avatarPreview || userProfile?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=128`}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.firstName || 'S')}+${encodeURIComponent(userProfile?.lastName || 'eller')}&background=22C55E&color=fff&size=128`;
                  }}
                />
              </div>

              {/* Settings Dropdown */}
              <div className="AutoboyExpress-dash-settings-wrapper">
                <button
                  className="AutoboyExpress-dash-settings-btn"
                  onClick={() => {
                    setShowSettings(!showSettings);
                    setShowNotifications(false);
                  }}
                >
                  <FontAwesomeIcon icon={faCog} />
                </button>

                {showSettings && (
                  <div className="AutoboyExpress-dash-dropdown AutoboyExpress-dash-settings-dropdown">
                    <div className="AutoboyExpress-dash-dropdown-content">
                      <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('settings')}>
                        <FontAwesomeIcon icon={faUserCircle} />
                        <span>My Profile</span>
                      </button>
                      <button className="AutoboyExpress-dash-dropdown-item" onClick={() => setActiveSection('settings')}>
                        <FontAwesomeIcon icon={faCog} />
                        <span>Settings</span>
                      </button>
                      <button className="AutoboyExpress-dash-dropdown-item">
                        <FontAwesomeIcon icon={faShieldAlt} />
                        <span>Privacy & Security</span>
                      </button>
                      <button className="AutoboyExpress-dash-dropdown-item">
                        <FontAwesomeIcon icon={faQuestionCircle} />
                        <span>Help & Support</span>
                      </button>
                      <div className="AutoboyExpress-dash-dropdown-divider"></div>
                      <button className="AutoboyExpress-dash-dropdown-item AutoboyExpress-dash-logout-btn" onClick={handleLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="AutoboyExpress-dash-content-area">
            {getContentForSection()}
          </div>
        </div>

        {/* Premium Upgrade Modal */}
        <PremiumUpgradeModal
          isOpen={showPremiumUpgrade}
          onClose={() => setShowPremiumUpgrade(false)}
          userType="seller"
        />
      </div>
    </>
  );
};

export default SellerDashboard;
